import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCcw, Send, ShieldCheck, Copy, UserPlus, Link2, ChevronDown, ChevronUp, Activity, AlertTriangle, Search, CalendarClock, Download, Upload, Database } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { VERIFIED_PURCHASE_EMAIL_HTML, VERIFIED_PURCHASE_EMAIL_PREVIEW, VERIFIED_PURCHASE_EMAIL_SUBJECT } from "@/constants/verifiedPurchaseEmailTemplate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusClass = {
  active: "border-emerald-400/40 bg-emerald-50 text-emerald-700",
  grace: "border-amber-400/40 bg-amber-50 text-amber-700",
  inactive: "border-black/10 bg-black/5 text-slate-500",
  expired: "border-rose-400/40 bg-rose-50 text-rose-600",
};

const purchaseStatusClass = {
  claimable: "border-black/15 bg-black/5 text-slate-700",
  claimed: "border-amber-400/40 bg-amber-50 text-amber-700",
  joined: "border-emerald-400/40 bg-emerald-50 text-emerald-700",
  unverified: "border-rose-400/40 bg-rose-50 text-rose-600",
};

const purchaseStatusLabel = {
  claimable: "Not claimed",
  claimed: "Claimed · not joined",
  joined: "Inside group",
  unverified: "Not verified",
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");

const triggerCsvDownload = (csvContent, fileName) => {
  if (typeof document === "undefined") return;

  const blob = new Blob(["\uFEFF", csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);

  try {
    link.click();
  } catch (error) {
    window.open(url, "_blank");
  } finally {
    window.setTimeout(() => {
      link.remove();
      URL.revokeObjectURL(url);
    }, 1000);
  }
};

const Admin = () => {
  const [adminKey, setAdminKey] = useState("");
  const [operatorName, setOperatorName] = useState("");
  const [memberships, setMemberships] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [removalPagination, setRemovalPagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [pendingRemovals, setPendingRemovals] = useState([]);
  const [pendingRemovalPagination, setPendingRemovalPagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [pendingRemovalQuery, setPendingRemovalQuery] = useState("");
  const [pendingRemovalPage, setPendingRemovalPage] = useState(1);
  const [removingMembershipId, setRemovingMembershipId] = useState("");
  const [removalQuery, setRemovalQuery] = useState("");
  const [removalType, setRemovalType] = useState("");
  const [removalDateFrom, setRemovalDateFrom] = useState("");
  const [removalDateTo, setRemovalDateTo] = useState("");
  const [removalRemovedAt, setRemovalRemovedAt] = useState("");
  const [removalName, setRemovalName] = useState("");
  const [removalEmail, setRemovalEmail] = useState("");
  const [removalPhone, setRemovalPhone] = useState("");
  const [removalContactIdentifier, setRemovalContactIdentifier] = useState("");
  const [removalTelegramId, setRemovalTelegramId] = useState("");
  const [removalMarket, setRemovalMarket] = useState("gold");
  const [removalChatId, setRemovalChatId] = useState("");
  const [removalJoined, setRemovalJoined] = useState("");
  const [removalExpiry, setRemovalExpiry] = useState("");
  const [removalGraceUntil, setRemovalGraceUntil] = useState("");
  const [removalMembershipDays, setRemovalMembershipDays] = useState("");
  const [removalMembershipSource, setRemovalMembershipSource] = useState("");
  const [removalPaymentStatus, setRemovalPaymentStatus] = useState("");
  const [removalLastPaymentAt, setRemovalLastPaymentAt] = useState("");
  const [removalPaymentAmount, setRemovalPaymentAmount] = useState("");
  const [removalTransactionId, setRemovalTransactionId] = useState("");
  const [removalAffiliate, setRemovalAffiliate] = useState("");
  const [removalAutopayActive, setRemovalAutopayActive] = useState(false);
  const [removalAutopayKnown, setRemovalAutopayKnown] = useState(false);
  const [removalReason, setRemovalReason] = useState("");
  const [removalFormBusy, setRemovalFormBusy] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [tab, setTab] = useState("overview");
  const [graceDays, setGraceDays] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [reissueIdentifier, setReissueIdentifier] = useState("");
  const [reissueManualOverride, setReissueManualOverride] = useState(false);
  const [reissueOverrideReason, setReissueOverrideReason] = useState("");
  const [reissueLink, setReissueLink] = useState("");
  const [reissueBusy, setReissueBusy] = useState(false);
  const [teamInviteLabel, setTeamInviteLabel] = useState("");
  const [teamInviteSlots, setTeamInviteSlots] = useState("5");
  const [teamInviteUserIds, setTeamInviteUserIds] = useState("");
  const [teamInviteLink, setTeamInviteLink] = useState("");
  const [teamInviteBusy, setTeamInviteBusy] = useState(false);
  const [affName, setAffName] = useState("");
  const [affEmail, setAffEmail] = useState("");
  const [affPassword, setAffPassword] = useState("");
  const [sellerCredentials, setSellerCredentials] = useState({});
  const [lookupSellerCode, setLookupSellerCode] = useState("");
  const [lookupPhone, setLookupPhone] = useState("");
  const [lookupMembers, setLookupMembers] = useState([]);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [affBusy, setAffBusy] = useState(false);
  const [expanded, setExpanded] = useState("");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [memberSellerFilter, setMemberSellerFilter] = useState("all");
  const [membershipSellerAssignments, setMembershipSellerAssignments] = useState({});
  const [sellerAssignmentBusy, setSellerAssignmentBusy] = useState("");
  const [manualMemberId, setManualMemberId] = useState("");
  const [manualMemberEmail, setManualMemberEmail] = useState("");
  const [manualMemberPhone, setManualMemberPhone] = useState("");
  const [manualMemberMarket, setManualMemberMarket] = useState("gold");
  const [manualMemberBusy, setManualMemberBusy] = useState(false);
  const [manualMemberInvite, setManualMemberInvite] = useState("");
  const [manualMemberInviteError, setManualMemberInviteError] = useState("");
  const [botStatus, setBotStatus] = useState(null);
  const [operations, setOperations] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditSearch, setAuditSearch] = useState("");
  const [auditQuery, setAuditQuery] = useState("");
  const [auditPagination, setAuditPagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [loading, setLoading] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [databaseExportBusy, setDatabaseExportBusy] = useState(false);
  const [databaseExportJob, setDatabaseExportJob] = useState(null);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [duplicateFilter, setDuplicateFilter] = useState("all");
  const [memberPagination, setMemberPagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [purchasePagination, setPurchasePagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [duplicateSummary, setDuplicateSummary] = useState({ verified: 0, unverified: 0 });
  const [affiliatePagination, setAffiliatePagination] = useState({ page: 1, per_page: 25, total: 0, pages: 0 });
  const [reconcileReason, setReconcileReason] = useState("");
  const [paidCsvFile, setPaidCsvFile] = useState(null);
  const [paidCsvConfirmed, setPaidCsvConfirmed] = useState(false);
  const [paidCsvBusy, setPaidCsvBusy] = useState(false);
  const paidCsvInputRef = useRef(null);
  const [singleRemovalMode, setSingleRemovalMode] = useState("phone");
  const [singleRemovalValue, setSingleRemovalValue] = useState("");
  const [singleRemovalMatches, setSingleRemovalMatches] = useState([]);
  const [singleRemovalBusy, setSingleRemovalBusy] = useState(false);
  const [removingSingleMemberId, setRemovingSingleMemberId] = useState("");
  const [membershipCsvFile, setMembershipCsvFile] = useState(null);
  const [membershipCsvMatchMode, setMembershipCsvMatchMode] = useState("both");
  const [membershipCsvPreview, setMembershipCsvPreview] = useState(null);
  const [membershipCsvConfirmed, setMembershipCsvConfirmed] = useState(false);
  const [membershipCsvBusy, setMembershipCsvBusy] = useState(false);
  const membershipCsvInputRef = useRef(null);
  const [verifiedEmailSubject, setVerifiedEmailSubject] = useState(VERIFIED_PURCHASE_EMAIL_SUBJECT);
  const [verifiedEmailPreview, setVerifiedEmailPreview] = useState(VERIFIED_PURCHASE_EMAIL_PREVIEW);
  const [verifiedEmailHtml, setVerifiedEmailHtml] = useState(VERIFIED_PURCHASE_EMAIL_HTML);
  const [verifiedEmailQrUrl, setVerifiedEmailQrUrl] = useState("");
  const [verifiedEmailConfirmed, setVerifiedEmailConfirmed] = useState(false);
  const [verifiedEmailBusy, setVerifiedEmailBusy] = useState(false);

  const visibleMemberships = memberships.filter((member) => {
    if (memberSellerFilter === "all") return true;
    if (memberSellerFilter === "direct" || memberSellerFilter === "manual") {
      return member.attribution_source === memberSellerFilter;
    }
    return member.affiliate === memberSellerFilter;
  });
  const membershipCsvRemovableCount = new Set((membershipCsvPreview?.rows || [])
    .filter((row) => row.status === "expired_in_group" && row.membership_id)
    .map((row) => row.membership_id)).size;

  const headers = () => ({ "X-Admin-Key": adminKey.trim(), "X-Admin-Actor": operatorName.trim() });

  const exportMembers = async () => {
    setExportBusy(true);
    setError("");
    setMessage("");
    const params = {
      page: 1,
      per_page: 100,
      q: query,
      date_from: dateFrom,
      date_to: dateTo,
      status: statusFilter,
      affiliate: memberSellerFilter === "all" ? "" : memberSellerFilter,
    };
    try {
      const firstPage = await axios.get(`${API}/admin/memberships`, { headers: headers(), params });
      const allMembers = [...(firstPage.data.memberships || [])];
      const pageCount = firstPage.data.pagination?.pages || 1;
      for (let exportPage = 2; exportPage <= pageCount; exportPage += 1) {
        const response = await axios.get(`${API}/admin/memberships`, {
          headers: { "X-Admin-Key": adminKey.trim(), "X-Admin-Actor": operatorName.trim() },
          params: { ...params, page: exportPage },
        });
        allMembers.push(...(response.data.memberships || []));
      }

      const columns = [
        ["Telegram user ID", "telegram_user_id"],
        ["Email", "email"],
        ["Phone", "phone"],
        ["Contact identifier", "contact_identifier"],
        ["Market", "market"],
        ["Chat ID", "chat_id"],
        ["Status", "status"],
        ["Joined", "joined"],
        ["Joined at", "joined_at"],
        ["Expires at", "expires_at"],
        ["Grace until", "grace_until"],
        ["Payment status", "payment_status"],
        ["Last payment at", "last_payment_at"],
        ["Last transaction ID", "last_transaction_id"],
        ["Renewal count", "renewal_count"],
        ["Autopay active", "autopay_active"],
        ["Autopay status known", "autopay_status_known"],
        ["Affiliate code", "affiliate"],
        ["Affiliate name", "affiliate_name"],
        ["Attribution source", "attribution_source"],
        ["Added by", "added_by"],
        ["Added at", "added_at"],
        ["Manual override", "manual_override"],
        ["Override reason", "manual_override_reason"],
      ];
      const escapeCell = (value) => {
        const text = value == null ? "" : String(value);
        const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
        return `"${safeText.replaceAll('"', '""')}"`;
      };
      const csv = [
        columns.map(([label]) => escapeCell(label)).join(","),
        ...allMembers.map((member) => columns.map(([, key]) => escapeCell(member[key])).join(",")),
      ].join("\r\n");
      triggerCsvDownload(csv, `admin-members-${new Date().toISOString().slice(0, 10)}.csv`);
      setMessage(`Downloaded ${allMembers.length} member records as CSV.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not export member data.");
    }
    setExportBusy(false);
  };

  const exportPurchases = async () => {
    setExportBusy(true);
    setError("");
    setMessage("");
    const params = {
      page: 1,
      per_page: 100,
      q: query,
      date_from: dateFrom,
      date_to: dateTo,
      status: statusFilter,
      affiliate: sellerFilter === "all" ? "" : sellerFilter,
      duplicate: duplicateFilter,
      min_amount: minAmount === "" ? undefined : minAmount,
      max_amount: maxAmount === "" ? undefined : maxAmount,
    };
    try {
      const firstPage = await axios.get(`${API}/admin/purchases`, { headers: headers(), params });
      const allPurchases = [...(firstPage.data.purchases || [])];
      const pageCount = firstPage.data.pagination?.pages || 1;
      for (let exportPage = 2; exportPage <= pageCount; exportPage += 1) {
        const response = await axios.get(`${API}/admin/purchases`, {
          headers: headers(),
          params: { ...params, page: exportPage },
        });
        allPurchases.push(...(response.data.purchases || []));
      }

      const columns = [
        ["Buyer name", "buyer_name"],
        ["Email", "buyer_email"],
        ["Phone", "buyer_phone"],
        ["Transaction ID", "transaction_id"],
        ["Offering", "offering"],
        ["Amount", "amount"],
        ["Provider event", "event"],
        ["Payment verified", "payment_verified"],
        ["Verification source", "verification_source"],
        ["Verification reason", "verification_reason"],
        ["Status", "status"],
        ["Market", "claimed_market"],
        ["Seller code", "affiliate"],
        ["Seller name", "affiliate_name"],
        ["Payment received at", "received_at"],
        ["Invite issued at", "used_at"],
        ["Group joined at", "joined_at"],
      ];
      const escapeCell = (value) => {
        const text = value == null ? "" : String(value);
        const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
        return `"${safeText.replaceAll('"', '""')}"`;
      };
      const csv = [
        columns.map(([label]) => escapeCell(label)).join(","),
        ...allPurchases.map((purchase) => columns.map(([, key]) => escapeCell(purchase[key])).join(",")),
      ].join("\r\n");
      triggerCsvDownload(csv, `admin-payments-${new Date().toISOString().slice(0, 10)}.csv`);
      setMessage(`Downloaded ${allPurchases.length} payment records as CSV.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not export payment data.");
    }
    setExportBusy(false);
  };

  const exportRemovals = async () => {
    setExportBusy(true);
    setError("");
    setMessage("");
    const params = {
      page: 1,
      per_page: 100,
      q: removalQuery,
      removal_type: removalType,
      date_from: removalDateFrom,
      date_to: removalDateTo,
    };
    try {
      const firstPage = await axios.get(`${API}/admin/membership-removals`, { headers: headers(), params });
      const allRemovals = [...(firstPage.data.removals || [])];
      const pageCount = firstPage.data.pagination?.pages || 1;
      for (let exportPage = 2; exportPage <= pageCount; exportPage += 1) {
        const response = await axios.get(`${API}/admin/membership-removals`, {
          headers: headers(),
          params: { ...params, page: exportPage },
        });
        allRemovals.push(...(response.data.removals || []));
      }

      const columns = [
        ["Removal record ID", "id"], ["Membership record ID", "membership_id"],
        ["Removed at", "removed_at"], ["Removal type", "removal_type"], ["Removed by", "removed_by"],
        ["Reason", "removal_reason"], ["Name", "name"], ["Email", "email"], ["Phone", "phone"],
        ["Contact identifier", "contact_identifier"], ["Telegram user ID", "telegram_user_id"],
        ["Market", "market"], ["Chat ID", "chat_id"],
        ["Membership joined at", "joined_at"], ["Membership expires at", "expires_at"],
        ["Grace until", "grace_until"], ["Membership days", "membership_days"],
        ["Membership source", "membership_source"], ["Payment status", "payment_status"],
        ["Last payment at", "last_payment_at"], ["Last payment amount", "last_payment_amount"],
        ["Last transaction ID", "last_transaction_id"], ["Affiliate", "affiliate"], ["Source", "source"],
        ["Autopay active", "autopay_active"], ["Autopay status known", "autopay_status_known"],
      ];
      const escapeCell = (value) => {
        const text = value == null ? "" : String(value);
        const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
        return `"${safeText.replaceAll('"', '""')}"`;
      };
      const csv = [
        columns.map(([label]) => escapeCell(label)).join(","),
        ...allRemovals.map((record) => columns.map(([, key]) => escapeCell(record[key])).join(",")),
      ].join("\r\n");
      triggerCsvDownload(csv, `membership-removals-${new Date().toISOString().slice(0, 10)}.csv`);
      setMessage(`Downloaded ${allRemovals.length} membership removal records as CSV.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not export membership removal data.");
    }
    setExportBusy(false);
  };

  const recordManualRemoval = async (event) => {
    event.preventDefault();
    setRemovalFormBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/admin/membership-removals`, {
        removal_type: "admin",
        removed_at: removalRemovedAt ? new Date(removalRemovedAt).toISOString() : "",
        name: removalName.trim(),
        email: removalEmail.trim(),
        phone: removalPhone.trim(),
        contact_identifier: removalContactIdentifier.trim(),
        telegram_user_id: removalTelegramId.trim() ? Number(removalTelegramId) : null,
        market: removalMarket,
        chat_id: removalChatId.trim() ? Number(removalChatId) : null,
        joined_at: removalJoined ? new Date(removalJoined).toISOString() : "",
        expires_at: removalExpiry ? new Date(`${removalExpiry}T00:00:00`).toISOString() : "",
        grace_until: removalGraceUntil ? new Date(`${removalGraceUntil}T00:00:00`).toISOString() : "",
        membership_days: removalMembershipDays ? Number(removalMembershipDays) : null,
        membership_source: removalMembershipSource.trim(),
        payment_status: removalPaymentStatus.trim(),
        last_payment_at: removalLastPaymentAt ? new Date(removalLastPaymentAt).toISOString() : "",
        last_payment_amount: removalPaymentAmount.trim(),
        last_transaction_id: removalTransactionId.trim(),
        affiliate: removalAffiliate.trim(),
        autopay_active: removalAutopayActive,
        autopay_status_known: removalAutopayKnown,
        removal_reason: removalReason.trim(),
      }, { headers: headers() });
      setRemovalName("");
      setRemovalEmail("");
      setRemovalPhone("");
      setRemovalContactIdentifier("");
      setRemovalTelegramId("");
      setRemovalChatId("");
      setRemovalRemovedAt("");
      setRemovalJoined("");
      setRemovalExpiry("");
      setRemovalGraceUntil("");
      setRemovalMembershipDays("");
      setRemovalMembershipSource("");
      setRemovalPaymentStatus("");
      setRemovalLastPaymentAt("");
      setRemovalPaymentAmount("");
      setRemovalTransactionId("");
      setRemovalAffiliate("");
      setRemovalAutopayActive(false);
      setRemovalAutopayKnown(false);
      setRemovalReason("");
      setMessage("Admin removal recorded.");
      await loadAll({ tab: "removals", page: 1 });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not record this member removal.");
    }
    setRemovalFormBusy(false);
  };

  const removeExpiredMember = async (member) => {
    const identity = member.name || member.phone || member.email || member.telegram_user_id;
    if (!window.confirm(`Remove ${identity} from the Telegram group? This member is past grace and has no verified renewal.`)) return;
    setRemovingMembershipId(member.id);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(
        `${API}/admin/membership-removals/${member.id}/remove`,
        {},
        { headers: headers() },
      );
      const status = response.data.status;
      setMessage(status === "removed"
        ? `Removed ${identity} from the group.`
        : status === "already_left"
          ? `${identity} had already left the group; their record was updated.`
          : status === "telegram_admin"
            ? `${identity} is a Telegram admin and was not removed.`
            : `Removal was not completed for ${identity} (${status || "unknown status"}).`);
      await loadAll({ tab: "removals", page: 1, pendingPage: 1 });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not remove this expired member.");
    }
    setRemovingMembershipId("");
  };

  const sendVerifiedBuyerEmail = async () => {
    if (!verifiedEmailConfirmed || !verifiedEmailSubject.trim() || !verifiedEmailHtml.trim()) return;
    if (!window.confirm("Send this email to every unique email address on a verified purchase? This action cannot be undone.")) return;
    setVerifiedEmailBusy(true);
    setError("");
    setMessage("");
    try {
      const started = await axios.post(`${API}/admin/purchases/send-verified-email`, {
        subject: verifiedEmailSubject,
        preview_text: verifiedEmailPreview,
        html: verifiedEmailHtml,
        qr_image_url: verifiedEmailQrUrl,
        confirm_all_verified: true,
      }, { headers: headers() });
      setVerifiedEmailConfirmed(false);
      let jobStatus = null;
      for (let attempt = 0; attempt < 180; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        const progress = await axios.get(`${API}/admin/purchases/sender-email-jobs/${started.data.job_id}`, { headers: headers() });
        jobStatus = progress.data;
        setMessage(`Sender email progress: ${jobStatus.sent_count} sent, ${jobStatus.failed_count} failed of ${jobStatus.recipient_count}.`);
        if (jobStatus.status === "completed") break;
      }
      if (jobStatus?.status === "completed") {
        setMessage(`Sender email complete: ${jobStatus.sent_count} of ${jobStatus.recipient_count} recipients sent${jobStatus.failed_count ? `, ${jobStatus.failed_count} failed` : ""}.`);
      } else {
        setMessage("Sender email is still processing. You can leave this page; the job will continue in the backend.");
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not send the verified-buyer email.");
    }
    setVerifiedEmailBusy(false);
  };

  const startDatabaseExport = async () => {
    setDatabaseExportBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(`${API}/admin/database-exports`, {}, { headers: headers() });
      setDatabaseExportJob(response.data);
      setMessage("Database export started. You can leave this page while it runs.");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not start the database export.");
    }
    setDatabaseExportBusy(false);
  };

  const downloadDatabaseExport = async () => {
    if (!databaseExportJob?.job_id) return;
    setDatabaseExportBusy(true);
    setError("");
    try {
      const response = await axios.post(
        `${API}/admin/database-exports/${databaseExportJob.job_id}/download-token`,
        {},
        { headers: headers() },
      );
      window.location.assign(
        `${API}/admin/database-exports/${databaseExportJob.job_id}/download?token=${encodeURIComponent(response.data.token)}`,
      );
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not download the database export.");
    }
    setDatabaseExportBusy(false);
  };

  const loadAll = async (overrides = {}) => {
    const activeTab = overrides.tab || tab;
    const activePage = overrides.page ?? page;
    setLoading(true);
    setError("");
    const filters = {
      page: activePage,
      per_page: 25,
      q: overrides.query ?? query,
      date_from: overrides.dateFrom ?? dateFrom,
      date_to: overrides.dateTo ?? dateTo,
    };
    const results = await Promise.allSettled([
      axios.get(`${API}/admin/memberships`, { headers: headers(), params: { ...filters, status: activeTab === "members" ? (overrides.status ?? statusFilter) : "", affiliate: (overrides.memberAffiliate ?? memberSellerFilter) === "all" ? "" : (overrides.memberAffiliate ?? memberSellerFilter) } }),
      axios.get(`${API}/admin/purchases`, { headers: headers(), params: {
        ...filters,
        status: activeTab === "purchases" ? (overrides.status ?? statusFilter) : "",
        affiliate: (overrides.purchaseAffiliate ?? sellerFilter) === "all" ? "" : (overrides.purchaseAffiliate ?? sellerFilter),
        duplicate: activeTab === "purchases" ? (overrides.duplicate ?? duplicateFilter) : "all",
        min_amount: activeTab === "purchases" ? (overrides.minAmount ?? minAmount) || undefined : undefined,
        max_amount: activeTab === "purchases" ? (overrides.maxAmount ?? maxAmount) || undefined : undefined,
      } }),
      axios.get(`${API}/admin/affiliates`, { headers: headers(), params: filters }),
      axios.get(`${API}/admin/operations`, { headers: headers() }),
      axios.get(`${API}/admin/webhook-events`, { headers: headers(), params: { page: 1, per_page: 8 } }),
      axios.get(`${API}/admin/audit`, { headers: headers(), params: { page: activeTab === "audit" ? activePage : 1, per_page: 25, q: overrides.auditQuery ?? auditQuery } }),
      activeTab === "removals"
        ? axios.get(`${API}/admin/membership-removals`, {
          headers: headers(),
          params: {
            page: activePage,
            per_page: 25,
            q: overrides.removalQuery ?? removalQuery,
            removal_type: overrides.removalType ?? removalType,
            date_from: overrides.removalDateFrom ?? removalDateFrom,
            date_to: overrides.removalDateTo ?? removalDateTo,
          },
        })
        : Promise.resolve({ data: null }),
      activeTab === "removals"
        ? axios.get(`${API}/admin/membership-removals/pending`, {
          headers: headers(),
          params: {
            page: overrides.pendingPage ?? pendingRemovalPage,
            per_page: 25,
            q: overrides.pendingRemovalQuery ?? pendingRemovalQuery,
          },
        })
        : Promise.resolve({ data: null }),
    ]);
    const [membersRes, purchasesRes, affiliatesRes, botStatusRes] = results;
    if (membersRes.status === "fulfilled") {
      setMemberships(membersRes.value.data.memberships || []);
      setGraceDays(membersRes.value.data.grace_period_days || 0);
      setMemberPagination(membersRes.value.data.pagination || memberPagination);
    }
    if (purchasesRes.status === "fulfilled") {
      setPurchases(purchasesRes.value.data.purchases || []);
      setPurchasePagination(purchasesRes.value.data.pagination || purchasePagination);
      setDuplicateSummary(purchasesRes.value.data.duplicate_summary || { verified: 0, unverified: 0 });
    }
    if (affiliatesRes.status === "fulfilled") {
      setAffiliates(affiliatesRes.value.data.affiliates || []);
      setAffiliatePagination(affiliatesRes.value.data.pagination || affiliatePagination);
    }
    if (botStatusRes.status === "fulfilled") {
      setOperations(botStatusRes.value.data);
      setBotStatus(botStatusRes.value.data.bot);
    }
    if (results[4].status === "fulfilled") setWebhookEvents(results[4].value.data.events || []);
    if (results[5].status === "fulfilled") {
      setAuditEvents(results[5].value.data.events || []);
      setAuditPagination(results[5].value.data.pagination || auditPagination);
    }
    if (results[6].status === "fulfilled" && results[6].value.data) {
      setRemovals(results[6].value.data.removals || []);
      setRemovalPagination(results[6].value.data.pagination || removalPagination);
    }
    if (results[7].status === "fulfilled" && results[7].value.data) {
      setPendingRemovals(results[7].value.data.members || []);
      setPendingRemovalPagination(results[7].value.data.pagination || pendingRemovalPagination);
      setPendingRemovalPage(results[7].value.data.pagination?.page || 1);
    }

    const failedSections = results.flatMap((result, index) => result.status === "rejected"
      ? [["Members", "Purchases", "Affiliates", "Operations", "Webhook diagnostics", "Audit history", "Membership removals", "Pending expiry removals"][index]]
      : []);
    if (failedSections.length >= 3) {
      setLoading(false);
      throw results.find((result) => result.status === "rejected").reason;
    }
    if (failedSections.length) {
      setError(`Some admin data could not load: ${failedSections.join(", ")}. Refresh to retry.`);
    }
    if (overrides.page !== undefined) setPage(overrides.page);
    setLoading(false);
  };

  useEffect(() => {
    if (!panelOpen || tab !== "members") return undefined;

    let refreshing = false;
    const refreshMemberships = async () => {
      if (refreshing) return;
      refreshing = true;
      try {
        const response = await axios.get(`${API}/admin/memberships`, {
          headers: { "X-Admin-Key": adminKey.trim(), "X-Admin-Actor": operatorName.trim() },
          params: {
            page,
            per_page: 25,
            q: query,
            date_from: dateFrom,
            date_to: dateTo,
            status: statusFilter,
            affiliate: memberSellerFilter === "all" ? "" : memberSellerFilter,
          },
        });
        setMemberships(response.data.memberships || []);
        setMemberPagination((current) => response.data.pagination || current);
      } catch {
      } finally {
        refreshing = false;
      }
    };

    const interval = window.setInterval(refreshMemberships, 15000);
    return () => window.clearInterval(interval);
  }, [panelOpen, tab, page, query, dateFrom, dateTo, statusFilter, memberSellerFilter, adminKey, operatorName]);

  useEffect(() => {
    const jobId = databaseExportJob?.job_id;
    if (!panelOpen || !jobId || !["queued", "exporting", "uploading"].includes(databaseExportJob.status)) return undefined;

    let cancelled = false;
    let timeoutId;
    const refreshExportStatus = async () => {
      try {
        const response = await axios.get(`${API}/admin/database-exports/${jobId}`, {
          headers: { "X-Admin-Key": adminKey.trim(), "X-Admin-Actor": operatorName.trim() },
        });
        if (cancelled) return;
        setDatabaseExportJob(response.data);
        if (["completed", "failed"].includes(response.data.status)) {
          setMessage(response.data.status === "completed"
            ? `Database export ready: ${response.data.documents_exported.toLocaleString()} documents across ${response.data.collection_count} collections.`
            : response.data.error || "Database export failed.");
          return;
        }
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.response?.data?.detail || "Could not refresh database export status.");
      }
      if (!cancelled) timeoutId = window.setTimeout(refreshExportStatus, 2000);
    };

    refreshExportStatus();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [panelOpen, databaseExportJob?.job_id, databaseExportJob?.status, adminKey, operatorName]);

  const openPanel = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await loadAll();
      setPanelOpen(true);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not open the admin panel");
    }
    setBusy(false);
  };

  const remind = async (telegramUserId) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/admin/remind`, { telegram_user_id: telegramUserId }, { headers: headers() });
      setMessage("Reminder sent");
      await loadAll();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not send the reminder");
    }
    setBusy(false);
  };

  const reissue = async (identifier = reissueIdentifier, manualOverride = reissueManualOverride, purchaseId = "") => {
    const normalizedIdentifier = String(identifier ?? "").trim();
    if (!normalizedIdentifier && !manualOverride && !purchaseId) {
      setError("Phone number or payment email required");
      return;
    }
    setReissueBusy(true);
    setError("");
    setMessage("");
    setReissueLink("");
    try {
      let resolvedPurchaseId = purchaseId;
      if (!resolvedPurchaseId && !normalizedIdentifier.includes("@")) {
        const phoneDigits = normalizedIdentifier.replace(/\D/g, "");
        if (phoneDigits.length >= 7) {
          const searchDigits = phoneDigits.slice(-10);
          const searchResponse = await axios.get(`${API}/admin/purchases`, {
            headers: headers(),
            params: { q: searchDigits, per_page: 100 },
          });
          const matches = (searchResponse.data.purchases || []).filter((purchase) => {
            const storedDigits = String(purchase.buyer_phone || "").replace(/\D/g, "");
            return purchase.payment_verified && storedDigits.endsWith(searchDigits);
          });
          if (matches.length > 1) {
            throw new Error("More than one verified purchase matches this number. Select the buyer from Payments & verification.");
          }
          if (matches.length === 1) resolvedPurchaseId = matches[0].id;
        }
      }
      const response = await axios.post(`${API}/admin/reissue`, {
        ...(resolvedPurchaseId
          ? { purchase_id: resolvedPurchaseId }
          : normalizedIdentifier.includes("@")
            ? { email: normalizedIdentifier.toLowerCase() }
            : normalizedIdentifier ? { phone: normalizedIdentifier } : {}),
        contact_identifier: manualOverride ? normalizedIdentifier : "",
        market: "gold",
        manual_override: manualOverride,
        override_reason: manualOverride ? reissueOverrideReason.trim() : "",
        operator_name: operatorName.trim(),
      }, { headers: headers() });
      setReissueLink(response.data.invite_url || response.data.bot_url);
      setMessage(response.data.manual_override
        ? "Direct group invite generated. Payment was not verified; this exception is logged. One person can use it within 30 minutes."
        : "Direct group invite generated. Send it to the buyer; one person can use it within 30 minutes.");
      try {
        await loadAll();
      } catch (refreshError) {
        setError("The invite was generated, but admin data could not refresh. The link is ready to copy.");
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || "Could not reissue the link.");
    }
    setReissueBusy(false);
  };

  const generateTeamInvite = async () => {
    const memberLimit = Number(teamInviteSlots);
    if (!Number.isInteger(memberLimit) || memberLimit < 1 || memberLimit > 50) {
      setError("Choose between 1 and 50 team members");
      return;
    }
    const enteredUserIds = teamInviteUserIds.split(",").map((value) => value.trim()).filter(Boolean);
    if (enteredUserIds.some((value) => !/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(Number(value)))) {
      setError("Enter valid numeric Telegram user IDs separated by commas");
      return;
    }
    const telegramUserIds = [...new Set(enteredUserIds.map(Number))];
    if (telegramUserIds.length > memberLimit) {
      setError("The number of Telegram IDs cannot exceed the team member limit");
      return;
    }
    setTeamInviteBusy(true);
    setError("");
    setMessage("");
    setTeamInviteLink("");
    const label = teamInviteLabel.trim();
    try {
      const response = await axios.post(`${API}/admin/reissue`, {
        contact_identifier: label || "Team member",
        market: "gold",
        manual_override: true,
        override_reason: label ? `Team access: ${label}` : "Team member access",
        operator_name: operatorName.trim(),
        member_limit: memberLimit,
        expires_in_minutes: 1440,
        team_invite: true,
        team_invite_telegram_ids: telegramUserIds,
      }, { headers: headers() });
      setTeamInviteLink(response.data.invite_url);
      setMessage(`Team invite ready for ${response.data.member_limit} members. It expires in 24 hours; each join is logged.`);
      setTeamInviteLabel("");
      setTeamInviteUserIds("");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || requestError.message || "Could not create a team invite.");
    }
    setTeamInviteBusy(false);
  };

  const lookupSellerMember = async (event) => {
    event.preventDefault();
    setLookupBusy(true);
    setError("");
    setLookupMembers([]);
    try {
      const response = await axios.get(`${API}/admin/seller-members`, {
        headers: headers(),
        params: { code: lookupSellerCode, phone: lookupPhone },
      });
      setLookupMembers(response.data.members || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not find this seller referral.");
    }
    setLookupBusy(false);
  };

  const createAffiliate = async () => {
    setAffBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/admin/affiliates`, {
        name: affName.trim(),
        seller_email: affEmail.trim().toLowerCase(),
        seller_password: affPassword,
      }, { headers: headers() });
      setAffName("");
      setAffEmail("");
      setAffPassword("");
      setMessage("Seller created — copy their link below and share it.");
      await loadAll({ tab: "affiliates", page: 1 });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not create the seller.");
    }
    setAffBusy(false);
  };

  const saveSellerCredentials = async (sellerCode) => {
    const credentials = sellerCredentials[sellerCode] || {};
    const currentSeller = affiliates.find((affiliate) => affiliate.code === sellerCode);
    setAffBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.put(`${API}/admin/affiliates/${sellerCode}/account`, {
        email: (credentials.email ?? currentSeller?.seller_email ?? "").trim().toLowerCase(),
        password: credentials.password || "",
        enabled: true,
      }, { headers: headers() });
      setSellerCredentials((current) => ({ ...current, [sellerCode]: { email: credentials.email, password: "" } }));
      setMessage("Seller login enabled. Share the email and temporary password with the seller securely.");
      await loadAll({ tab: "affiliates" });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not save seller login.");
    }
    setAffBusy(false);
  };

  const addManualMember = async () => {
    if (!manualMemberId.trim()) {
      setError("Telegram user ID is required");
      return;
    }
    setManualMemberBusy(true);
    setError("");
    setMessage("");
    setManualMemberInvite("");
    setManualMemberInviteError("");
    try {
      const response = await axios.post(`${API}/admin/memberships/add`, {
        telegram_user_id: Number(manualMemberId.trim()),
        email: manualMemberEmail.trim().toLowerCase(),
        phone: manualMemberPhone.trim(),
        market: manualMemberMarket,
        operator_name: operatorName.trim(),
      }, { headers: headers() });
      setManualMemberId("");
      setManualMemberEmail("");
      setManualMemberPhone("");
      setManualMemberMarket("gold");
      setManualMemberInvite(response.data.invite_url || "");
      setManualMemberInviteError(response.data.invite_error || "");
      setMessage(response.data.joined
        ? "Member was already in the group; membership tracking is active."
        : response.data.invite_url
          ? "Member is tracked. Share the one-use invite below; they must open it to join."
          : "Member is tracked, but the bot could not create an invite.");
      await loadAll();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not add member.");
    }
    setManualMemberBusy(false);
  };

  const assignMembershipSeller = async (member) => {
    const affiliateCode = membershipSellerAssignments[member.id] ?? member.affiliate ?? "";
    setSellerAssignmentBusy(member.id);
    setError("");
    setMessage("");
    try {
      await axios.put(`${API}/admin/memberships/${member.id}/seller`, {
        affiliate_code: affiliateCode,
      }, { headers: headers() });
      setMembershipSellerAssignments((current) => Object.fromEntries(
        Object.entries(current).filter(([membershipId]) => membershipId !== member.id),
      ));
      setMessage(affiliateCode ? "Member seller attribution updated." : "Member marked as Direct.");
      await loadAll();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not update seller attribution.");
    }
    setSellerAssignmentBusy("");
  };

  const reconcilePurchase = async (purchaseId) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/admin/purchases/reconcile`, {
        purchase_id: purchaseId,
        reason: reconcileReason.trim(),
      }, { headers: headers() });
      setMessage("Payment reconciled from stored provider evidence.");
      setReconcileReason("");
      await loadAll({ tab: "purchases" });
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not reconcile this payment.");
    }
    setBusy(false);
  };

  const importPaidCsv = async () => {
    if (!paidCsvFile || !paidCsvConfirmed || paidCsvBusy) return;
    setPaidCsvBusy(true);
    setError("");
    setMessage("");
    const formData = new FormData();
    formData.append("file", paidCsvFile);
    try {
      const response = await axios.post(`${API}/admin/purchases/import-paid-csv`, formData, {
        headers: headers(),
        timeout: 10 * 60 * 1000,
      });
      const rows = response.data.rows || [];
      const csvCell = (value) => {
        const text = value == null ? "" : String(value);
        const safeText = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
        return `"${safeText.replaceAll('"', '""')}"`;
      };
      const csv = [
        ["Phone", "Email", "Bot link", "Status", "Verification source"].map(csvCell).join(","),
        ...rows.map((row) => [row.phone, row.email, row.bot_url, row.status, row.verification_source || "admin_csv"].map(csvCell).join(",")),
      ].join("\r\n");
      triggerCsvDownload(csv, `admin-paid-user-bot-links-${new Date().toISOString().slice(0, 10)}.csv`);
      const summary = response.data.summary || {};
      setMessage(`Import complete: ${summary.bot_links_ready || 0} bot links downloaded; ${summary.invalid_phones || 0} invalid phones, ${summary.duplicate_rows || 0} duplicate rows, ${summary.already_joined || 0} already joined.`);
      setPaidCsvFile(null);
      setPaidCsvConfirmed(false);
      if (paidCsvInputRef.current) paidCsvInputRef.current.value = "";
      try {
        await loadAll({ tab: "purchases", page: 1 });
      } catch (refreshError) {
        setError("Import succeeded and the links CSV downloaded, but the purchase list could not refresh.");
      }
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not import the paid-user CSV.");
    }
    setPaidCsvBusy(false);
  };

  const lookupSingleMemberForRemoval = async (event) => {
    event.preventDefault();
    if (!singleRemovalValue.trim() || singleRemovalBusy) return;
    setSingleRemovalBusy(true);
    setSingleRemovalMatches([]);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(`${API}/admin/memberships/lookup-single`, {
        mode: singleRemovalMode,
        value: singleRemovalValue.trim(),
      }, { headers: headers() });
      setSingleRemovalMatches(response.data.matches || []);
      setMessage(response.data.match_count
        ? `Found ${response.data.match_count} active membership record(s). Review the group and expiry before removing.`
        : "No active, joined membership matched that identifier.");
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not look up this member.");
    }
    setSingleRemovalBusy(false);
  };

  const removeSingleMember = async (member) => {
    const identity = member.name || member.phone || member.email || member.telegram_user_id;
    if (!window.confirm(`Remove ${identity} from ${member.market || "the Telegram group"}?`)) return;
    setRemovingSingleMemberId(member.membership_id);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(
        `${API}/admin/memberships/${member.membership_id}/remove-manually`,
        {},
        { headers: headers() },
      );
      const status = response.data.status;
      setSingleRemovalMatches((current) => current.map((item) => item.membership_id === member.membership_id
        ? { ...item, status }
        : item));
      setMessage(status === "removed"
        ? `Removed ${identity} from the group.`
        : status === "telegram_admin"
          ? `${identity} is a Telegram admin and was not removed.`
          : status === "already_left"
            ? `${identity} had already left the group.`
            : `Removal was not completed (${status || "unknown status"}).`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not remove this member.");
    }
    setRemovingSingleMemberId("");
  };

  const matchMembershipCsv = async () => {
    if (!membershipCsvFile || membershipCsvBusy) return;
    setMembershipCsvBusy(true);
    setMembershipCsvPreview(null);
    setMembershipCsvConfirmed(false);
    setError("");
    setMessage("");
    const formData = new FormData();
    formData.append("file", membershipCsvFile);
    formData.append("match_mode", membershipCsvMatchMode);
    try {
      const response = await axios.post(`${API}/admin/memberships/match-expired-csv`, formData, {
        headers: headers(),
        timeout: 10 * 60 * 1000,
      });
      setMembershipCsvPreview(response.data);
      const summary = response.data.summary || {};
      setMessage(`Expiry match complete: ${summary.expired_in_group || 0} expired members in the group; ${summary.not_expired || 0} not yet expired; ${summary.telegram_admins || 0} Telegram admins protected.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not match this membership CSV.");
    }
    setMembershipCsvBusy(false);
  };

  const removeMembershipCsvMatches = async () => {
    const matches = (membershipCsvPreview?.rows || []).filter((row) => row.status === "expired_in_group" && row.membership_id);
    if (!matches.length || !membershipCsvConfirmed || membershipCsvBusy) return;
    if (!window.confirm(`Remove ${matches.length} matched member record(s) from the Telegram group or channel? Telegram admins are excluded.`)) return;
    setMembershipCsvBusy(true);
    setError("");
    setMessage("");
    try {
      const response = await axios.post(`${API}/admin/memberships/remove-csv-matches`, {
        membership_ids: [...new Set(matches.map((row) => row.membership_id))],
      }, { headers: headers(), timeout: 10 * 60 * 1000 });
      const statuses = new Map((response.data.results || []).map((result) => [result.membership_id, result.status]));
      setMembershipCsvPreview((current) => current && ({
        ...current,
        rows: current.rows.map((row) => statuses.has(row.membership_id)
          ? { ...row, status: statuses.get(row.membership_id) }
          : row),
        summary: {
          ...current.summary,
          expired_in_group: current.rows.filter((row) => row.status === "expired_in_group" && !statuses.has(row.membership_id)).length,
          telegram_admins: current.rows.filter((row) => row.status === "telegram_admin").length + (response.data.summary?.telegram_admins || 0),
        },
      }));
      const summary = response.data.summary || {};
      setMessage(`Expiry removal complete: ${summary.removed || 0} removed; ${summary.not_expired || 0} not expired; ${summary.verified_renewal || 0} verified renewals skipped; ${summary.telegram_admins || 0} admins protected.`);
      setMembershipCsvConfirmed(false);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not remove the matched members.");
    }
    setMembershipCsvBusy(false);
  };

  const retryBotAction = async (eventId) => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/admin/bot-actions/${eventId}/retry`, {}, { headers: headers() });
      setMessage("Failed reminder delivered on retry.");
      await loadAll();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Could not retry this bot action.");
    }
    setBusy(false);
  };

  const openQueue = async (nextTab, nextStatus = "") => {
    setTab(nextTab);
    setStatusFilter(nextStatus);
    setQuery("");
    setDateFrom("");
    setDateTo("");
    setMinAmount("");
    setMaxAmount("");
    setMemberSellerFilter("all");
    setSellerFilter("all");
    setDuplicateFilter("all");
    setPage(1);
    await loadAll({
      tab: nextTab,
      page: 1,
      status: nextStatus,
      query: "",
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: "",
      memberAffiliate: "all",
      purchaseAffiliate: "all",
      duplicate: "all",
    });
  };

  const applyFilters = async () => {
    setPage(1);
    await loadAll({ page: 1 });
  };

  const changePage = async (nextPage) => {
    await loadAll({ page: nextPage });
  };

  const currentPagination = tab === "members"
    ? memberPagination
    : tab === "purchases" ? purchasePagination : tab === "removals" ? removalPagination : affiliatePagination;

  const renderPagination = () => (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-pagination">
      <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
        {currentPagination.total} records · page {currentPagination.page} of {Math.max(1, currentPagination.pages)}
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={() => changePage(Math.max(1, page - 1))} disabled={loading || page <= 1} className="rounded-lg border border-black/10 px-3 py-2 text-xs text-slate-600 disabled:opacity-40">Previous</button>
        <button type="button" onClick={() => changePage(Math.min(currentPagination.pages || 1, page + 1))} disabled={loading || page >= currentPagination.pages} className="rounded-lg border border-black/10 px-3 py-2 text-xs text-slate-600 disabled:opacity-40">Next</button>
      </div>
    </div>
  );

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(text);
    setMessage(label);
  };

  return (
    <main className="horizon-page min-h-screen overflow-x-hidden px-5 py-10" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center gap-2.5">
          <BrandLogo className="text-lg" />
        </div>
        <p className="mt-6 font-mono2 text-[11px] uppercase tracking-[0.3em] text-slate-500">50 CR CLUB · Admin</p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#0a0a0a] sm:text-5xl" data-testid="admin-title">Member control</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">Track payments, access, renewals and seller referrals.</p>

        <div className="mt-8 grid gap-3 sm:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_auto]">
          <input
            type="password"
            value={adminKey}
            onChange={(event) => setAdminKey(event.target.value)}
            placeholder="Admin panel key"
            data-testid="admin-key-input"
            className="w-full max-w-sm rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a] outline-none focus:border-black/40"
          />
          <input type="text" value={operatorName} onChange={(event) => setOperatorName(event.target.value)} placeholder="Your name for the audit trail" data-testid="admin-operator-name" className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
          <button onClick={openPanel} disabled={busy || !adminKey.trim() || !operatorName.trim()} data-testid="admin-open-button" className="btn-main rounded-xl px-5 py-3 font-sub text-sm font-bold uppercase disabled:opacity-50">Open admin dashboard</button>
        </div>
        <p className="mt-2 text-xs text-slate-500">The shared admin key does not identify individual staff. Enter your name to attribute actions performed from this session.</p>

        {error && <p className="mt-4 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-600" data-testid="admin-error">{error}</p>}
        {message && <p className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="admin-message">{message}</p>}

        {panelOpen && <>
        <section className="mt-6 border-y border-black/10 py-5" data-testid="admin-team-invite">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-xl">
              <div className="flex items-center gap-2"><UserPlus className="h-4 w-4 text-emerald-700" /><h2 className="font-display text-lg font-bold text-[#0a0a0a]">Team group invite</h2></div>
              <p className="mt-1 text-sm text-slate-600">Create a direct invite for up to 50 team members. It expires in 24 hours, and each join is tracked as an admin override.</p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Team label <input value={teamInviteLabel} onChange={(event) => setTeamInviteLabel(event.target.value)} maxLength={80} placeholder="Optional" className="mt-1 block w-44 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Members <input type="number" min="1" max="50" step="1" value={teamInviteSlots} onChange={(event) => setTeamInviteSlots(event.target.value)} className="mt-1 block w-24 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Banned member Telegram IDs <input value={teamInviteUserIds} onChange={(event) => setTeamInviteUserIds(event.target.value)} placeholder="Optional, comma-separated" className="mt-1 block w-60 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <button type="button" onClick={generateTeamInvite} disabled={teamInviteBusy} data-testid="admin-team-invite-generate" className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Link2 className="h-4 w-4" />{teamInviteBusy ? "Generating…" : "Generate link"}</button>
            </div>
          </div>
          {teamInviteLink && <div className="mt-4 flex flex-col gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:flex-row sm:items-center" data-testid="admin-team-invite-result"><p className="min-w-0 flex-1 break-all font-mono2 text-xs text-emerald-900">{teamInviteLink}</p><button type="button" onClick={() => copyText(teamInviteLink, "Team invite copied.")} className="flex shrink-0 items-center gap-2 rounded-lg border border-emerald-700/20 bg-white px-3 py-2 text-xs font-semibold text-emerald-800"><Copy className="h-3.5 w-3.5" />Copy invite</button></div>}
        </section>
        <div className="mt-10 flex flex-wrap gap-2" data-testid="admin-tabs">
          {[ ["overview", "Admin overview"], ["members", "Memberships"], ["purchases", "Payments & verification"], ["affiliates", "Seller management"], ["removals", "Member removals"], ["audit", "Action history"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => { setTab(id); setPage(1); setStatusFilter(""); setMemberSellerFilter("all"); setSellerFilter("all"); loadAll({ tab: id, page: 1, status: "", memberAffiliate: "all", purchaseAffiliate: "all" }); }}
              className={`rounded-full px-5 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-colors ${tab === id ? "bg-[#0a0a0a] text-white" : "border border-black/10 bg-white text-slate-500 hover:text-[#0a0a0a]"}`}
            >
              {label}
            </button>
          ))}
        </div>
        {tab !== "overview" && tab !== "audit" && tab !== "removals" && <div className="glass mt-4 flex flex-wrap items-end gap-3 rounded-xl p-4" data-testid="admin-filters">
          <label className="min-w-[220px] flex-1 font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Search phone, email, transaction
            <span className="mt-1 flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3"><Search className="h-4 w-4 text-slate-400" /><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") applyFilters(); }} placeholder="Search records" className="min-w-0 flex-1 py-2 text-sm normal-case tracking-normal text-[#0a0a0a] outline-none" /></span>
          </label>
          <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">From<input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]" /></label>
          <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">To<input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]" /></label>
          {tab !== "affiliates" && <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Status<select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]">
            <option value="">All statuses</option>
            {tab === "members" ? <><option value="active">Active</option><option value="grace">Grace</option><option value="expired">Expired</option><option value="inactive">Inactive</option><option value="not_joined">Not joined</option><option value="expiring">Expiring in 7 days</option></> : <><option value="verified">Verified payments</option><option value="claimable">Ready to claim</option><option value="claimed">Claimed, not joined</option><option value="joined">Joined</option><option value="unverified">Unverified</option></>}
          </select></label>}
          {tab === "members" && <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Seller<select data-testid="admin-members-seller-filter" value={memberSellerFilter} onChange={(event) => { const value = event.target.value; setMemberSellerFilter(value); setPage(1); loadAll({ page: 1, memberAffiliate: value }); }} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]"><option value="all">All sellers</option><option value="direct">Direct</option><option value="manual">Manual add</option>{affiliates.map((affiliate) => <option key={affiliate.code} value={affiliate.code}>{affiliate.name}</option>)}</select></label>}
          {tab === "purchases" && <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Seller<select data-testid="admin-purchases-seller-filter" value={sellerFilter} onChange={(event) => { const value = event.target.value; setSellerFilter(value); setPage(1); loadAll({ page: 1, purchaseAffiliate: value }); }} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]"><option value="all">All sellers</option><option value="direct">Direct</option>{affiliates.map((affiliate) => <option key={affiliate.code} value={affiliate.code}>{affiliate.name}</option>)}</select></label>}
          {tab === "purchases" && <>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Min amount<input type="number" min="0" step="0.01" value={minAmount} onChange={(event) => setMinAmount(event.target.value)} placeholder="No minimum" className="mt-1 block w-32 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]" /></label>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Max amount<input type="number" min="0" step="0.01" value={maxAmount} onChange={(event) => setMaxAmount(event.target.value)} placeholder="No maximum" className="mt-1 block w-32 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]" /></label>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Duplicates<select value={duplicateFilter} onChange={(event) => { const value = event.target.value; setDuplicateFilter(value); setPage(1); loadAll({ page: 1, duplicate: value }); }} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-xs text-[#0a0a0a]"><option value="all">All payments</option><option value="verified">Verified duplicates</option><option value="unverified">Unverified duplicates</option><option value="hide">Hide duplicates</option></select></label>
          </>}
          <button type="button" onClick={applyFilters} disabled={loading} className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Search className="h-3.5 w-3.5" /> Apply</button>
          <button type="button" onClick={() => { setQuery(""); setDateFrom(""); setDateTo(""); setMinAmount(""); setMaxAmount(""); setStatusFilter(""); setDuplicateFilter("all"); setMemberSellerFilter("all"); setSellerFilter("all"); setPage(1); loadAll({ page: 1, status: "", query: "", dateFrom: "", dateTo: "", minAmount: "", maxAmount: "", memberAffiliate: "all", purchaseAffiliate: "all", duplicate: "all" }); }} disabled={loading} className="rounded-lg border border-black/10 px-4 py-2.5 text-xs text-slate-600 disabled:opacity-50">Clear</button>
        </div>}
        {operations && tab === "overview" && (
          <section className="mt-6 space-y-5" data-testid="admin-operations-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-bold text-[#0a0a0a]">Operations overview</h2>
              <span className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400">{loading ? "Refreshing…" : "Live data"}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ["Payments · 24h", operations.counts.recent_payments, "purchases", ""],
                ["Unverified webhooks", operations.counts.unverified_webhooks, "purchases", "unverified"],
                ["Ready to claim", operations.counts.claimable, "purchases", "claimable"],
                ["Not joined", operations.counts.claimed_not_joined, "members", "not_joined"],
                ["Expiring · 7 days", operations.counts.expiring_memberships, "members", "expiring"],
              ].map(([label, count, targetTab, targetStatus]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => openQueue(targetTab, targetStatus)}
                  className="glass rounded-xl p-4 text-left transition-colors hover:border-violet-300 hover:bg-violet-50/40"
                >
                  <span className="block font-mono2 text-[9px] uppercase tracking-wider text-slate-500">{label}</span>
                  <span className="mt-2 block font-display text-2xl font-bold text-[#0a0a0a]">{count}</span>
                </button>
              ))}
            </div>
            <section className="glass rounded-xl p-4" data-testid="admin-database-export">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2"><Database className="h-4 w-4 text-emerald-700" /><h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600">Database export</h3></div>
                  <p className="mt-1 text-xs text-slate-500">Downloads every application collection as Extended JSONL files in a ZIP archive.</p>
                </div>
                <button
                  type="button"
                  onClick={startDatabaseExport}
                  disabled={databaseExportBusy || ["queued", "exporting", "uploading"].includes(databaseExportJob?.status)}
                  data-testid="admin-database-export-start"
                  className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"
                >
                  <Database className="h-4 w-4" />
                  {databaseExportBusy ? "Starting…" : ["queued", "exporting", "uploading"].includes(databaseExportJob?.status) ? "Export running…" : "Export all data"}
                </button>
              </div>
              {databaseExportJob && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-black/5 pt-3" data-testid="admin-database-export-status">
                  <p className="text-xs text-slate-600" role="status">
                    {databaseExportJob.status === "completed"
                      ? `${databaseExportJob.documents_exported.toLocaleString()} documents · ${databaseExportJob.collection_count} collections · ${(databaseExportJob.file_size_bytes / (1024 * 1024)).toFixed(1)} MB`
                      : databaseExportJob.status === "failed"
                        ? databaseExportJob.error || "Export failed. Start a new export to retry."
                        : `${databaseExportJob.status === "uploading" ? "Preparing download" : databaseExportJob.current_collection ? `Exporting ${databaseExportJob.current_collection}` : "Preparing export"} · ${databaseExportJob.collections_completed || 0}/${databaseExportJob.collection_count || "…"} collections · ${(databaseExportJob.documents_exported || 0).toLocaleString()} documents`}
                  </p>
                  {databaseExportJob.status === "completed" && (
                    <button
                      type="button"
                      onClick={downloadDatabaseExport}
                      disabled={databaseExportBusy}
                      data-testid="admin-database-export-download"
                      className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:opacity-50"
                    >
                      <Download className="h-4 w-4" /> {databaseExportBusy ? "Preparing…" : "Download ZIP"}
                    </button>
                  )}
                </div>
              )}
            </section>
            <div className="grid gap-4 lg:grid-cols-3">
              <div className={`rounded-xl border px-4 py-4 text-sm ${operations.bot.running ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`} data-testid="admin-bot-status">
                <div className="flex items-center gap-2 font-semibold text-[#0a0a0a]"><Activity className="h-4 w-4" /> @{operations.bot.bot_username} · {operations.bot.running ? "poller lease active" : operations.bot.configured ? "not polling" : "not configured"}</div>
                <dl className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                  <div><dt className="text-slate-400">Poller holder</dt><dd className="break-all">{operations.bot.holder || "None"}</dd></div>
                  <div><dt className="text-slate-400">Last heartbeat</dt><dd>{formatDate(operations.bot.last_heartbeat)}</dd></div>
                  <div><dt className="text-slate-400">Last Telegram update</dt><dd>{formatDate(operations.bot.last_update_at)}</dd></div>
                  <div><dt className="text-slate-400">Last error</dt><dd className="break-words">{operations.bot.last_error || "None recorded"}</dd></div>
                </dl>
              </div>
              <div className="glass rounded-xl p-4">
                <h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">Recent bot failures</h3>
                {operations.recent_failures.length ? operations.recent_failures.slice(0, 4).map((failure, index) => (
                  <div key={`${failure.created_at}-${index}`} className="mt-2 flex flex-wrap items-center gap-2 text-xs text-rose-700"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /><span className="min-w-0 flex-1">{failure.action}: {failure.message}</span>{failure.retryable && <button type="button" onClick={() => retryBotAction(failure.id)} disabled={busy} className="rounded-md border border-rose-300 px-2 py-1 text-[9px] font-semibold uppercase disabled:opacity-40">Retry reminder</button>}</div>
                )) : <p className="mt-2 text-xs text-slate-500">No failed bot actions recorded.</p>}
              </div>
              <div className="glass rounded-xl p-4" data-testid="admin-recent-payments">
                <h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">Latest payments</h3>
                {operations.recent_payments.map((payment, index) => (
                  <button type="button" key={`${payment.received_at}-${index}`} onClick={() => openQueue("purchases", payment.verified ? "" : "unverified")} className="mt-2 flex w-full items-center justify-between gap-3 border-b border-black/5 pb-2 text-left last:border-0">
                    <span className="min-w-0"><span className="block truncate text-xs text-[#0a0a0a]">{payment.buyer}</span><span className="block truncate text-[10px] text-slate-400">{payment.event || "Payment received"}</span></span>
                    <span className={`shrink-0 text-xs font-semibold ${payment.verified ? "text-emerald-700" : "text-rose-700"}`}>₹{payment.amount || "-"}</span>
                  </button>
                ))}
                {!operations.recent_payments.length && <p className="mt-2 text-xs text-slate-500">No payment events yet.</p>}
              </div>
            </div>
            <div className="glass overflow-x-auto rounded-xl p-4" data-testid="admin-webhook-diagnostics">
              <div className="mb-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /><h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600">Recent redacted webhook events</h3></div>
              <table className="w-full min-w-[620px] text-left text-xs">
                <thead className="text-[9px] uppercase tracking-widest text-slate-400"><tr><th className="py-2">Received</th><th>Event/status</th><th>Transaction</th><th>Decision</th><th>Details</th></tr></thead>
                <tbody className="divide-y divide-black/5">{webhookEvents.map((event) => (
                  <tr key={event.id}><td className="py-2 pr-3">{formatDate(event.received_at)}</td><td className="pr-3">{(event.status_signals || [event.event]).join(" · ")}</td><td className="pr-3">{event.transaction_id || "-"}</td><td className={event.payment_verified ? "text-emerald-700" : "text-rose-700"}>{event.payment_verified ? "Verified" : "Unverified"}</td><td>{event.verification_reason}</td></tr>
                ))}{!webhookEvents.length && <tr><td colSpan="5" className="py-3 text-slate-500">No webhook events recorded yet.</td></tr>}</tbody>
              </table>
            </div>
         <section className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/70 p-4" data-testid="admin-paid-csv-import">
          <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-emerald-700" /><h2 className="font-display text-base font-bold text-[#0a0a0a]">Import paid users by phone</h2></div>
          <p className="mt-1 text-sm text-slate-600">Upload a CSV with a phone column. Each valid number is marked admin-verified, and receives a unique bot claim link in the downloaded CSV. The bot asks them to confirm the same phone before issuing a group invite.</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <input ref={paidCsvInputRef} type="file" accept=".csv,text/csv" aria-label="Choose paid users CSV" onChange={(event) => { setPaidCsvFile(event.target.files?.[0] || null); setPaidCsvConfirmed(false); }} className="max-w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border file:border-black/10 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700" />
            <label className="flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={paidCsvConfirmed} onChange={(event) => setPaidCsvConfirmed(event.target.checked)} disabled={!paidCsvFile || paidCsvBusy} className="mt-0.5 h-4 w-4 accent-emerald-700" /><span>I confirm every listed user has paid; this import records my verification, not an AppX webhook.</span></label>
            <button type="button" onClick={importPaidCsv} disabled={!paidCsvFile || !paidCsvConfirmed || paidCsvBusy} className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Download className="h-4 w-4" />{paidCsvBusy ? "Importing…" : "Import and download links"}</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Accepted headers include phone, phone number, mobile, or mobile number. Re-uploading the same numbers reuses their existing active links.</p>
        </section>
         <section className="mt-5 rounded-xl border border-black/10 bg-white/80 p-4" data-testid="admin-verified-email">
          <div className="flex items-center gap-2"><Send className="h-4 w-4 text-emerald-700" /><h2 className="font-display text-base font-bold text-[#0a0a0a]">Email verified buyers</h2></div>
          <p className="mt-1 text-xs text-slate-600">Sends one Sender transactional email per unique email address across all verified purchases, not just the current page. Check consent before sending promotional content.</p>
          <div className="mt-3 grid gap-3">
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Subject<input value={verifiedEmailSubject} onChange={(event) => setVerifiedEmailSubject(event.target.value)} maxLength={200} data-testid="admin-verified-email-subject" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Preview text<input value={verifiedEmailPreview} onChange={(event) => setVerifiedEmailPreview(event.target.value)} maxLength={300} data-testid="admin-verified-email-preview" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">QR image URL (optional)<input type="url" value={verifiedEmailQrUrl} onChange={(event) => setVerifiedEmailQrUrl(event.target.value)} placeholder="https://... (hosted QR image)" data-testid="admin-verified-email-qr-url" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
            <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Email content (HTML)<textarea value={verifiedEmailHtml} onChange={(event) => setVerifiedEmailHtml(event.target.value)} maxLength={100000} rows={18} data-testid="admin-verified-email-html" className="mt-1 block w-full resize-y rounded-lg border border-black/10 bg-white px-3 py-2 font-mono2 text-xs normal-case tracking-normal text-[#0a0a0a]" /></label>
            <p className="text-[11px] text-slate-500">Sender identity comes from server secrets: SENDER_FROM_EMAIL (verified Sender address) and SENDER_FROM_NAME. The QR section is omitted unless you provide a hosted HTTPS image URL.</p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={verifiedEmailConfirmed} onChange={(event) => setVerifiedEmailConfirmed(event.target.checked)} disabled={verifiedEmailBusy} data-testid="admin-verified-email-confirm" className="mt-0.5 h-4 w-4 accent-emerald-700" /><span>I confirm this message should go to all verified buyers with an email address.</span></label>
              <button type="button" onClick={sendVerifiedBuyerEmail} disabled={verifiedEmailBusy || !verifiedEmailConfirmed || !verifiedEmailSubject.trim() || !verifiedEmailHtml.trim()} data-testid="admin-verified-email-send" className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Send className="h-3.5 w-3.5" />{verifiedEmailBusy ? "Sending…" : "Send to verified buyers"}</button>
            </div>
          </div>
        </section>
          </section>
        )}

        {tab === "members" && <>
        <div className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
          <div className="flex items-center gap-3">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0a0a0a] text-white"><UserPlus className="h-4 w-4" /></span>
            <div>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">Admin add member</p>
              <p className="mt-1 text-sm text-slate-600">Use the team invite above for a quick group link. This form is for tracking a known Telegram user ID and creating their personal invite.</p>
            </div>
          </div>
          {manualMemberInvite && <div className="mt-4 flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center" data-testid="admin-manual-member-invite"><div className="min-w-0 flex-1"><p className="font-mono2 text-[9px] uppercase tracking-widest text-emerald-800">Personal invite · expires in 5 minutes · one use</p><p className="mt-1 break-all font-mono2 text-xs text-emerald-900">{manualMemberInvite}</p></div><button type="button" onClick={() => copyText(manualMemberInvite, "Invite copied. Send it to the member; they must open it to join.")} className="rounded-lg border border-emerald-700/20 bg-white px-3 py-2 text-xs font-semibold text-emerald-800">Copy invite</button></div>}
          {manualMemberInviteError && <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">Tracking was created, but invite creation failed: {manualMemberInviteError}</p>}
          <div className="mt-5 grid gap-3 md:grid-cols-5">
            <input value={manualMemberId} onChange={(event) => setManualMemberId(event.target.value)} type="number" placeholder="Telegram user ID" className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
            <input value={manualMemberEmail} onChange={(event) => setManualMemberEmail(event.target.value)} type="email" placeholder="Email (optional)" className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
            <input value={manualMemberPhone} onChange={(event) => setManualMemberPhone(event.target.value)} type="tel" placeholder="Phone (+91...)" className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
            <select value={manualMemberMarket} onChange={(event) => setManualMemberMarket(event.target.value)} className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm text-[#0a0a0a] outline-none focus:border-black/40">
              <option value="gold">Gold</option>
              <option value="stocks">Stocks</option>
              <option value="crypto">Crypto</option>
            </select>
            <button onClick={addManualMember} disabled={manualMemberBusy || !manualMemberId.trim()} className="btn-main rounded-xl px-5 py-2.5 font-sub text-xs font-bold uppercase tracking-wide disabled:opacity-50">{manualMemberBusy ? "Adding..." : "Add member"}</button>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="glass rounded-2xl p-5"><p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">Members shown</p><p className="mt-2 font-display text-3xl text-[#0a0a0a]">{memberships.length}</p></div>
          <div className="glass rounded-2xl p-5"><p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">Grace period</p><p className="mt-2 font-display text-3xl text-amber-600">{graceDays} days</p></div>
          <div className="glass rounded-2xl p-5"><p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-500">Autopay active</p><p className="mt-2 font-display text-3xl text-emerald-600">{memberships.filter((member) => member.autopay_active).length}</p></div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500">
            {visibleMemberships.length} member{visibleMemberships.length === 1 ? "" : "s"} shown
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={exportMembers}
              disabled={exportBusy || loading}
              title="Export all members matching the current filters as CSV"
              data-testid="admin-members-export"
              className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 font-mono2 text-xs font-semibold text-slate-700 transition-colors hover:border-black/40 disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              {exportBusy ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>

        <div className="glass mt-4 overflow-x-auto rounded-2xl">
          <table className="w-full min-w-[1280px] text-left">
            <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-5 py-4">Member</th><th className="px-5 py-4">Seller / added by</th><th className="px-5 py-4">Room</th><th className="px-5 py-4">Payment</th><th className="px-5 py-4">Joined</th><th className="px-5 py-4">Expires</th><th className="px-5 py-4">Autopay</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Action</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {visibleMemberships.map((member) => (
                <tr key={member.id} className="text-sm text-slate-600">
                  <td className="px-5 py-4"><p className="text-[#0a0a0a]">{member.phone || member.contact_identifier || member.email || "Telegram member"}</p>{member.email && member.phone && <p className="mt-1 text-xs text-slate-500">{member.email}</p>}<p className="mt-1 font-mono2 text-[10px] text-slate-400">{member.telegram_user_id}</p></td>
                  <td className="px-5 py-4">
                    {member.manual_override ? (
                      <div>
                        <p className="font-medium text-rose-700">Admin override · unpaid/unverified</p>
                        {member.manual_override_reason && <p className="mt-1 text-xs text-slate-500">{member.manual_override_reason}</p>}
                        <p className="mt-1 text-[10px] text-slate-400">Added by {member.added_by || "Legacy record · actor unavailable"} · {formatDate(member.added_at)}</p>
                      </div>
                    ) : member.attribution_source === "manual" ? (
                      <div>
                        <p className="font-medium text-slate-700">Manual add</p>
                        <p className="mt-1 text-[10px] text-slate-500">Added by {member.added_by || "Legacy record · actor unavailable"}</p>
                        <p className="mt-1 text-[10px] text-slate-400">{formatDate(member.added_at)}</p>
                      </div>
                    ) : member.affiliate ? (
                      <div>
                        <p className="font-medium text-[#0a0a0a]">{member.affiliate_name || member.affiliate}</p>
                        <p className="mt-1 font-mono2 text-[9px] text-slate-400">{member.affiliate}</p>
                      </div>
                    ) : (
                      <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
                        {member.attribution_source === "manual" ? "Manual add" : "Direct"}
                      </span>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <select
                        aria-label={`Seller attribution for ${member.telegram_user_id || member.email || member.phone || "member"}`}
                        value={membershipSellerAssignments[member.id] ?? member.affiliate ?? ""}
                        onChange={(event) => setMembershipSellerAssignments((current) => ({ ...current, [member.id]: event.target.value }))}
                        disabled={sellerAssignmentBusy === member.id}
                        className="max-w-40 rounded-md border border-black/10 bg-white px-2 py-1.5 font-mono2 text-[10px] text-slate-700"
                      >
                        <option value="">Direct</option>
                        {member.affiliate && !affiliates.some((affiliate) => affiliate.code === member.affiliate) && <option value={member.affiliate}>{member.affiliate_name || member.affiliate}</option>}
                        {affiliates.map((affiliate) => <option key={affiliate.code} value={affiliate.code}>{affiliate.name}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => assignMembershipSeller(member)}
                        disabled={sellerAssignmentBusy === member.id || (membershipSellerAssignments[member.id] ?? member.affiliate ?? "") === (member.affiliate ?? "")}
                        className="rounded-md border border-black/10 px-2 py-1.5 text-[9px] font-semibold uppercase text-slate-600 disabled:opacity-40"
                      >
                        {sellerAssignmentBusy === member.id ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-4 uppercase">{member.market || "-"}</td>
                  <td className="px-5 py-4"><span className={`inline-flex rounded-full border px-2.5 py-1 font-mono2 text-[9px] font-semibold uppercase ${["paid", "admin_verified"].includes(member.payment_status) ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-rose-300 bg-rose-50 text-rose-600"}`}>{member.payment_status === "manual_override" ? "Manual override · not paid" : member.payment_status === "admin_verified" ? "paid" : member.payment_status || "unverified"}</span>{member.last_payment_at && <p className="mt-1 text-[10px] text-slate-400">{formatDate(member.last_payment_at)}</p>}{member.last_transaction_id && <p className="mt-1 font-mono2 text-[9px] text-slate-400">TXN {member.last_transaction_id}</p>}</td>
                  <td className="px-5 py-4">{member.joined ? <span className="text-emerald-600">In group</span> : <span className="text-slate-400">Not joined</span>}</td>
                  <td className="px-5 py-4"><p>{formatDate(member.expires_at)}</p>{member.grace_until && <p className="mt-1 text-xs text-amber-600">Grace until {formatDate(member.grace_until)}</p>}</td>
                  <td className="px-5 py-4">{!member.autopay_status_known ? <span className="text-slate-500">Unknown</span> : member.autopay_active ? <span className="text-emerald-600">Enabled</span> : <span className="text-amber-600">Not enabled</span>}</td>
                  <td className="px-5 py-4"><span className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-semibold uppercase ${statusClass[member.status] || statusClass.inactive}`}>{member.status}</span></td>
                  <td className="px-5 py-4"><button onClick={() => remind(member.telegram_user_id)} disabled={busy || member.status === "inactive"} className="flex items-center gap-2 rounded-lg border border-black/10 px-3 py-2 text-xs text-slate-600 transition-colors hover:border-black/40 hover:text-[#0a0a0a] disabled:opacity-40"><Send className="h-3.5 w-3.5" /> Remind</button></td>
                </tr>
              ))}
              {!visibleMemberships.length && <tr><td colSpan="9" className="px-5 py-12 text-center text-sm text-slate-500">No members match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
        </>}

        {tab === "removals" && (
          <section className="mt-6 space-y-5" data-testid="admin-membership-removals">
            <section className="rounded-xl border border-black/10 bg-white/80 p-4" data-testid="admin-single-member-removal">
              <div className="flex items-center gap-2"><Search className="h-4 w-4 text-rose-700" /><h2 className="font-display text-base font-bold text-[#0a0a0a]">Remove one member by phone or email</h2></div>
              <p className="mt-1 text-sm text-slate-600">Find active, joined membership records, review the Telegram status, then remove a selected member. Telegram admins are protected.</p>
              <form onSubmit={lookupSingleMemberForRemoval} className="mt-3 flex flex-wrap items-end gap-3">
                <div className="inline-flex rounded-lg border border-black/10 bg-white p-1" role="group" aria-label="Single member lookup field" data-testid="admin-single-removal-mode">
                  {[ ["phone", "Phone"], ["email", "Email"] ].map(([mode, label]) => <button key={mode} type="button" onClick={() => { setSingleRemovalMode(mode); setSingleRemovalMatches([]); }} disabled={singleRemovalBusy} aria-pressed={singleRemovalMode === mode} data-testid={`admin-single-removal-mode-${mode}`} className={`rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${singleRemovalMode === mode ? "bg-[#0a0a0a] text-white" : "text-slate-600 hover:bg-black/5"}`}>{label}</button>)}
                </div>
                <label className="min-w-[220px] flex-1 font-mono2 text-[9px] uppercase tracking-widest text-slate-500">{singleRemovalMode === "phone" ? "Phone number" : "Email address"}<input value={singleRemovalValue} onChange={(event) => setSingleRemovalValue(event.target.value)} type={singleRemovalMode === "email" ? "email" : "tel"} maxLength={320} data-testid="admin-single-removal-value" placeholder={singleRemovalMode === "phone" ? "+91…" : "name@example.com"} className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <button type="submit" disabled={singleRemovalBusy || !singleRemovalValue.trim()} data-testid="admin-single-removal-search" className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Search className="h-3.5 w-3.5" />{singleRemovalBusy ? "Searching…" : "Find member"}</button>
              </form>
              {!!singleRemovalMatches.length && <div className="mt-4 overflow-x-auto rounded-lg border border-black/10 bg-white">
                <table className="w-full min-w-[760px] text-left text-xs"><thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[9px] uppercase tracking-widest text-slate-500"><tr><th className="px-3 py-2">Member</th><th className="px-3 py-2">Group</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Telegram status</th><th className="px-3 py-2">Action</th></tr></thead>
                  <tbody className="divide-y divide-black/5">{singleRemovalMatches.map((member) => <tr key={member.membership_id} className="text-slate-600"><td className="px-3 py-2"><p>{member.phone || member.email || `Telegram ${member.telegram_user_id}`}</p>{member.email && member.phone && <p className="mt-1 text-slate-400">{member.email}</p>}</td><td className="px-3 py-2">{member.market || member.chat_id || "-"}</td><td className="px-3 py-2">{formatDate(member.expires_at)}</td><td className={`px-3 py-2 font-semibold ${member.status === "in_group" ? "text-emerald-700" : member.status === "telegram_admin" ? "text-amber-700" : "text-slate-500"}`}>{{ in_group: "In group", telegram_admin: "Telegram admin · protected", already_left: "Already left", check_failed: "Telegram check failed", missing_telegram_identity: "Missing Telegram ID", removed: "Removed" }[member.status] || member.status}</td><td className="px-3 py-2"><button type="button" onClick={() => removeSingleMember(member)} disabled={member.status !== "in_group" || removingSingleMemberId === member.membership_id} data-testid={`admin-single-removal-button-${member.membership_id}`} className="rounded-md border border-rose-300 bg-rose-50 px-3 py-1.5 font-semibold text-rose-800 disabled:opacity-40">{removingSingleMemberId === member.membership_id ? "Removing…" : "Remove"}</button></td></tr>)}</tbody>
                </table>
              </div>}
            </section>

            <section className="rounded-xl border border-rose-200 bg-rose-50/60 p-4" data-testid="admin-membership-csv-tool">
              <div className="flex items-center gap-2"><Upload className="h-4 w-4 text-rose-700" /><h2 className="font-display text-base font-bold text-[#0a0a0a]">Match expired members from CSV</h2></div>
              <p className="mt-1 text-sm text-slate-600">Choose phone, email, or both. Only members whose expiry date is before today in India time can be selected. The bot rechecks expiry, renewal, and Telegram admin status before removing anyone.</p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <input ref={membershipCsvInputRef} type="file" accept=".csv,text/csv" aria-label="Choose membership match CSV" data-testid="admin-membership-csv-file" onChange={(event) => { setMembershipCsvFile(event.target.files?.[0] || null); setMembershipCsvPreview(null); setMembershipCsvConfirmed(false); }} className="max-w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border file:border-black/10 file:bg-white file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700" />
                <div className="inline-flex rounded-lg border border-black/10 bg-white p-1" role="group" aria-label="CSV match mode" data-testid="admin-membership-csv-mode">
                  {[ ["phone", "Phone"], ["email", "Email"], ["both", "Both"] ].map(([mode, label]) => <button key={mode} type="button" onClick={() => { setMembershipCsvMatchMode(mode); setMembershipCsvPreview(null); setMembershipCsvConfirmed(false); }} disabled={membershipCsvBusy} aria-pressed={membershipCsvMatchMode === mode} data-testid={`admin-membership-csv-mode-${mode}`} className={`rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50 ${membershipCsvMatchMode === mode ? "bg-[#0a0a0a] text-white" : "text-slate-600 hover:bg-black/5"}`}>{label}</button>)}
                </div>
                <button type="button" onClick={matchMembershipCsv} disabled={!membershipCsvFile || membershipCsvBusy} data-testid="admin-membership-csv-match" className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50">{membershipCsvBusy ? "Matching…" : "Match CSV"}</button>
                <label className="flex items-start gap-2 text-xs text-slate-700"><input type="checkbox" checked={membershipCsvConfirmed} onChange={(event) => setMembershipCsvConfirmed(event.target.checked)} disabled={!membershipCsvPreview || membershipCsvBusy || !membershipCsvPreview.rows.some((row) => row.status === "expired_in_group")} data-testid="admin-membership-csv-confirm" className="mt-0.5 h-4 w-4 accent-rose-700" /><span>I reviewed the expired matches and confirm removal.</span></label>
                <button type="button" onClick={removeMembershipCsvMatches} disabled={!membershipCsvConfirmed || membershipCsvBusy || !membershipCsvRemovableCount} data-testid="admin-membership-csv-remove" className="flex items-center gap-2 rounded-lg border border-rose-300 bg-white px-4 py-2.5 font-sub text-xs font-bold uppercase text-rose-800 disabled:opacity-50">{membershipCsvBusy ? "Working…" : `Remove matched members${membershipCsvRemovableCount ? ` (${membershipCsvRemovableCount})` : ""}`}</button>
              </div>
              {membershipCsvPreview && <div className="mt-4 overflow-x-auto rounded-lg border border-black/10 bg-white" data-testid="admin-membership-csv-preview">
                <table className="w-full min-w-[780px] text-left text-xs">
                  <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[9px] uppercase tracking-widest text-slate-500"><tr><th className="px-3 py-2">CSV row</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Email</th><th className="px-3 py-2">Expires</th><th className="px-3 py-2">Telegram ID</th><th className="px-3 py-2">Group / market</th><th className="px-3 py-2">Match result</th></tr></thead>
                  <tbody className="divide-y divide-black/5">{membershipCsvPreview.rows.map((row) => <tr key={`${row.row}-${row.membership_id || row.status}`} className="text-slate-600"><td className="px-3 py-2">{row.row}</td><td className="px-3 py-2">{row.phone || "-"}</td><td className="px-3 py-2">{row.email || "-"}</td><td className="px-3 py-2">{formatDate(row.expires_at)}</td><td className="px-3 py-2">{row.telegram_user_id ?? "-"}</td><td className="px-3 py-2">{row.market || row.chat_id || "-"}</td><td className={`px-3 py-2 font-semibold ${row.status === "expired_in_group" ? "text-rose-700" : row.status === "telegram_admin" || row.status === "removed" ? "text-emerald-700" : "text-slate-500"}`}>{({ expired_in_group: "Expired · ready", in_group: "In group", telegram_admin: "Telegram admin · protected", already_left: "Already left", no_match: "No match", ambiguous: "Ambiguous · skipped", check_failed: "Telegram check failed", missing_identifier: "No phone/email", not_expired: "Not expired · skipped", missing_expiry: "No expiry date · skipped", verified_renewal: "Renewal verified · skipped", removed: "Removed", remove_failed: "Removal failed", no_longer_active: "No longer active", missing_telegram_identity: "Missing Telegram ID", invalid_membership_id: "Invalid member record" })[row.status] || row.status}</td></tr>)}</tbody>
                </table>
              </div>}
            </section>
            <div>
              <h2 className="font-display text-xl font-bold text-[#0a0a0a]">Member removal records</h2>
              <p className="mt-1 text-sm text-slate-600">Bot expiry removals are recorded automatically. Add a record for a member already removed by an admin; this form does not remove anyone from Telegram.</p>
            </div>

            <section className="glass overflow-hidden rounded-xl" data-testid="admin-pending-expiry-removals">
              <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black/10 p-4">
                <div>
                  <h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-700">Past grace · no verified renewal</h3>
                  <p className="mt-1 text-xs text-slate-500">Only active group members whose grace period ended are listed. Payment is rechecked before a kick.</p>
                </div>
                <form onSubmit={(event) => { event.preventDefault(); setPendingRemovalPage(1); loadAll({ tab: "removals", page: 1, pendingPage: 1 }); }} className="flex gap-2">
                  <input value={pendingRemovalQuery} onChange={(event) => setPendingRemovalQuery(event.target.value)} placeholder="Search name, email, phone, ID" aria-label="Search pending expired members" className="min-w-0 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm" />
                  <button type="submit" disabled={loading} className="btn-main rounded-lg px-3 py-2 text-xs font-bold uppercase disabled:opacity-50">Search</button>
                </form>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1250px] text-left text-xs">
                  <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[9px] uppercase tracking-widest text-slate-500"><tr><th className="px-4 py-3">Member</th><th className="px-4 py-3">Room</th><th className="px-4 py-3">Expiry / grace</th><th className="px-4 py-3">Last payment</th><th className="px-4 py-3">Seller</th><th className="px-4 py-3">Action</th></tr></thead>
                  <tbody className="divide-y divide-black/5">{pendingRemovals.map((member) => (
                    <tr key={member.id} className="text-slate-600"><td className="px-4 py-3"><p className="font-medium text-[#0a0a0a]">{member.name || member.phone || member.email || "Member"}</p>{member.email && <p className="mt-1">{member.email}</p>}{member.phone && <p className="mt-1">{member.phone}</p>}{member.contact_identifier && <p className="mt-1">{member.contact_identifier}</p>}{member.telegram_user_id != null && <p className="mt-1 font-mono2 text-[10px]">Telegram ID {member.telegram_user_id}</p>}</td><td className="px-4 py-3">{member.market || "-"}<p className="mt-1 text-slate-400">Chat {member.chat_id || "-"}</p></td><td className="px-4 py-3">Expires {formatDate(member.expires_at)}<p className="mt-1 text-rose-700">Grace ended {formatDate(member.grace_until)}</p><p className="mt-1">{member.membership_days ? `${member.membership_days} days` : ""}</p></td><td className="px-4 py-3">{formatDate(member.last_payment_at)}<p className="mt-1">{member.last_payment_amount || "-"}</p><p className="mt-1 font-mono2 text-[9px]">{member.last_transaction_id || ""}</p><p className="mt-1">Autopay {member.autopay_status_known ? (member.autopay_active ? "active" : "inactive") : "unknown"}</p></td><td className="px-4 py-3">{member.affiliate || "Direct"}</td><td className="px-4 py-3"><button type="button" onClick={() => removeExpiredMember(member)} disabled={loading || removingMembershipId === member.id || member.telegram_admin} title={member.telegram_admin ? "Telegram administrators are not removed" : "Recheck payment and remove from the Telegram group"} className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 font-mono2 text-[10px] font-semibold uppercase text-rose-800 disabled:opacity-40">{removingMembershipId === member.id ? "Removing…" : member.telegram_admin ? "Telegram admin" : "Remove from group"}</button></td></tr>
                  ))}{!pendingRemovals.length && <tr><td colSpan="6" className="px-4 py-10 text-center text-sm text-slate-500">No active group members are past grace without a verified renewal.</td></tr>}</tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-black/10 px-4 py-3 text-xs text-slate-500"><span>{pendingRemovalPagination.total} eligible · page {pendingRemovalPagination.page} of {Math.max(1, pendingRemovalPagination.pages)}</span><div className="flex gap-2"><button type="button" onClick={() => loadAll({ tab: "removals", page: 1, pendingPage: Math.max(1, pendingRemovalPage - 1) })} disabled={loading || pendingRemovalPage <= 1} className="rounded-md border border-black/10 px-3 py-2 disabled:opacity-40">Previous</button><button type="button" onClick={() => loadAll({ tab: "removals", page: 1, pendingPage: Math.min(pendingRemovalPagination.pages || 1, pendingRemovalPage + 1) })} disabled={loading || pendingRemovalPage >= pendingRemovalPagination.pages} className="rounded-md border border-black/10 px-3 py-2 disabled:opacity-40">Next</button></div></div>
            </section>

            <form onSubmit={recordManualRemoval} className="glass rounded-xl p-5" data-testid="admin-removal-entry-form">
              <h3 className="font-mono2 text-[10px] uppercase tracking-widest text-slate-600">Record admin removal</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <input value={removalName} onChange={(event) => setRemovalName(event.target.value)} maxLength={120} placeholder="Member name" aria-label="Member name" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalEmail} onChange={(event) => setRemovalEmail(event.target.value)} type="email" maxLength={320} placeholder="Email" aria-label="Member email" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalPhone} onChange={(event) => setRemovalPhone(event.target.value)} type="tel" maxLength={40} placeholder="Phone" aria-label="Member phone" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalContactIdentifier} onChange={(event) => setRemovalContactIdentifier(event.target.value)} maxLength={160} placeholder="Other contact identifier" aria-label="Other contact identifier" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalTelegramId} onChange={(event) => setRemovalTelegramId(event.target.value)} type="number" placeholder="Telegram user ID" aria-label="Telegram user ID" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <select value={removalMarket} onChange={(event) => setRemovalMarket(event.target.value)} aria-label="Membership market" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm"><option value="gold">Gold</option><option value="stocks">Stocks</option><option value="crypto">Crypto</option></select>
                <input value={removalChatId} onChange={(event) => setRemovalChatId(event.target.value)} type="number" placeholder="Telegram chat ID" aria-label="Telegram chat ID" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Removed at<input value={removalRemovedAt} onChange={(event) => setRemovalRemovedAt(event.target.value)} type="datetime-local" aria-label="Removal date and time" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Member joined<input value={removalJoined} onChange={(event) => setRemovalJoined(event.target.value)} type="datetime-local" aria-label="Membership joined at" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Membership expiry<input value={removalExpiry} onChange={(event) => setRemovalExpiry(event.target.value)} type="date" aria-label="Membership expiry date" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Grace ends<input value={removalGraceUntil} onChange={(event) => setRemovalGraceUntil(event.target.value)} type="date" aria-label="Grace period end date" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <input value={removalMembershipDays} onChange={(event) => setRemovalMembershipDays(event.target.value)} type="number" min="1" max="3660" placeholder="Membership days" aria-label="Membership days" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalMembershipSource} onChange={(event) => setRemovalMembershipSource(event.target.value)} maxLength={80} placeholder="Membership source" aria-label="Membership source" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalPaymentStatus} onChange={(event) => setRemovalPaymentStatus(event.target.value)} maxLength={80} placeholder="Payment status" aria-label="Payment status" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Last payment<input value={removalLastPaymentAt} onChange={(event) => setRemovalLastPaymentAt(event.target.value)} type="datetime-local" aria-label="Last payment date" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
                <input value={removalPaymentAmount} onChange={(event) => setRemovalPaymentAmount(event.target.value)} maxLength={40} placeholder="Last payment amount" aria-label="Last payment amount" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalTransactionId} onChange={(event) => setRemovalTransactionId(event.target.value)} maxLength={160} placeholder="Transaction ID" aria-label="Transaction ID" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <input value={removalAffiliate} onChange={(event) => setRemovalAffiliate(event.target.value)} maxLength={80} placeholder="Seller / affiliate code" aria-label="Seller or affiliate code" className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm" />
                <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={removalAutopayActive} onChange={(event) => setRemovalAutopayActive(event.target.checked)} className="h-4 w-4 accent-emerald-700" />Autopay active</label>
                <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={removalAutopayKnown} onChange={(event) => setRemovalAutopayKnown(event.target.checked)} className="h-4 w-4 accent-emerald-700" />Autopay status known</label>
                <textarea value={removalReason} onChange={(event) => setRemovalReason(event.target.value)} maxLength={500} minLength={3} required placeholder="Reason for removal" aria-label="Removal reason" rows={2} className="rounded-lg border border-black/10 bg-white px-3 py-2.5 text-sm sm:col-span-2" />
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-slate-500">Enter at least one member identifier. Leave “Removed at” blank to use the current time. Your admin operator name is stored with the record.</p>
                <button type="submit" disabled={removalFormBusy || (!removalName.trim() && !removalEmail.trim() && !removalPhone.trim() && !removalContactIdentifier.trim() && !removalTelegramId.trim())} className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50">{removalFormBusy ? "Recording…" : "Record removal"}</button>
              </div>
            </form>

            <div className="glass flex flex-wrap items-end gap-3 rounded-xl p-4" data-testid="admin-removal-filters">
              <label className="min-w-[220px] flex-1 font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Search name, email, phone, ID, or reason<input value={removalQuery} onChange={(event) => setRemovalQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); setPage(1); loadAll({ tab: "removals", page: 1 }); } }} placeholder="Search removal records" className="mt-1 block w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">Type<select value={removalType} onChange={(event) => setRemovalType(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]"><option value="">All removals</option><option value="expiry">Expiry</option><option value="admin">Admin</option></select></label>
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">From<input type="date" value={removalDateFrom} onChange={(event) => setRemovalDateFrom(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <label className="font-mono2 text-[9px] uppercase tracking-widest text-slate-500">To<input type="date" value={removalDateTo} onChange={(event) => setRemovalDateTo(event.target.value)} className="mt-1 block rounded-lg border border-black/10 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
              <button type="button" onClick={() => { setPage(1); loadAll({ tab: "removals", page: 1 }); }} disabled={loading} className="btn-main flex items-center gap-2 rounded-lg px-4 py-2.5 font-sub text-xs font-bold uppercase disabled:opacity-50"><Search className="h-3.5 w-3.5" /> Apply</button>
              <button type="button" onClick={() => { setRemovalQuery(""); setRemovalType(""); setRemovalDateFrom(""); setRemovalDateTo(""); setPage(1); loadAll({ tab: "removals", page: 1, removalQuery: "", removalType: "", removalDateFrom: "", removalDateTo: "" }); }} disabled={loading} className="rounded-lg border border-black/10 px-4 py-2.5 text-xs text-slate-600 disabled:opacity-50">Clear</button>
              <button type="button" onClick={exportRemovals} disabled={exportBusy || loading} data-testid="admin-removals-export" className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2.5 font-mono2 text-xs font-semibold text-slate-700 disabled:opacity-50"><Download className="h-4 w-4" />{exportBusy ? "Exporting…" : "Export CSV"}</button>
            </div>

            <div className="glass overflow-x-auto rounded-xl" data-testid="admin-removals-table">
              <table className="w-full min-w-[1450px] text-left text-xs">
                <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[9px] uppercase tracking-widest text-slate-500"><tr><th className="px-4 py-3">Removed</th><th className="px-4 py-3">Member</th><th className="px-4 py-3">Type / removed by</th><th className="px-4 py-3">Reason</th><th className="px-4 py-3">Room</th><th className="px-4 py-3">Membership</th><th className="px-4 py-3">Last payment</th><th className="px-4 py-3">Seller</th></tr></thead>
                <tbody className="divide-y divide-black/5">{removals.map((record) => (
                  <tr key={record.id} className="text-slate-600"><td className="whitespace-nowrap px-4 py-3">{formatDate(record.removed_at)}</td><td className="px-4 py-3"><p className="font-medium text-[#0a0a0a]">{record.name || record.phone || record.email || "Member"}</p>{record.email && <p className="mt-1">{record.email}</p>}{record.phone && <p className="mt-1">{record.phone}</p>}{record.contact_identifier && <p className="mt-1">{record.contact_identifier}</p>}{record.telegram_user_id != null && <p className="mt-1 font-mono2 text-[10px]">Telegram ID {record.telegram_user_id}</p>}</td><td className="px-4 py-3"><span className={`rounded-full border px-2 py-1 text-[9px] font-semibold uppercase ${record.removal_type === "expiry" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-rose-300 bg-rose-50 text-rose-700"}`}>{record.removal_type}</span><p className="mt-2">{record.removed_by || "-"}</p></td><td className="max-w-56 whitespace-normal px-4 py-3">{record.removal_reason || "-"}</td><td className="px-4 py-3">{record.market || "-"}<p className="mt-1 text-slate-400">{record.chat_id || ""}</p></td><td className="px-4 py-3">Joined {formatDate(record.joined_at)}<p className="mt-1">Expires {formatDate(record.expires_at)}</p><p className="mt-1">Grace until {formatDate(record.grace_until)}</p><p className="mt-1">{record.membership_days ? `${record.membership_days} days` : ""}</p><p className="mt-1">{record.membership_source || ""}</p><p className="mt-1">Payment {record.payment_status || "-"}</p></td><td className="px-4 py-3">{formatDate(record.last_payment_at)}<p className="mt-1">{record.last_payment_amount || ""}</p><p className="mt-1 font-mono2 text-[9px]">{record.last_transaction_id || ""}</p><p className="mt-1">Autopay {record.autopay_status_known ? (record.autopay_active ? "active" : "inactive") : "unknown"}</p></td><td className="px-4 py-3">{record.affiliate || "Direct"}</td></tr>
                ))}{!removals.length && <tr><td colSpan="8" className="px-4 py-12 text-center text-sm text-slate-500">No membership removals match these filters.</td></tr>}</tbody>
              </table>
            </div>
          </section>
        )}

        {tab === "purchases" && (
        <>
        <div className="mt-5 flex flex-col gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-end">
          <label className="flex-1 font-mono2 text-[9px] uppercase tracking-widest text-amber-800">Reconciliation reason<input value={reconcileReason} onChange={(event) => setReconcileReason(event.target.value)} maxLength={500} placeholder="Explain why this verified provider event should be reconciled" className="mt-1 block w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm normal-case tracking-normal text-[#0a0a0a]" /></label>
          <span className="flex items-center gap-1 text-[10px] text-amber-800"><ShieldCheck className="h-3.5 w-3.5" /> Requires qualifying stored event evidence</span>
        </div>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500">
              {purchases.length} payment{purchases.length === 1 ? "" : "s"} logged
            </p>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-amber-800">
              {duplicateSummary.verified} verified duplicates
            </span>
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-rose-700">
              {duplicateSummary.unverified} unverified duplicates
            </span>
          </div>
          <button
            type="button"
            onClick={exportPurchases}
            disabled={exportBusy || loading}
            title="Export all payments matching the current filters as CSV"
            data-testid="admin-purchases-export"
            className="flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 font-mono2 text-xs font-semibold text-slate-700 transition-colors hover:border-black/40 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportBusy ? "Exporting…" : "Export CSV"}
          </button>
        </div>

        <div className="glass mt-4 overflow-x-auto rounded-2xl" data-testid="admin-purchases-table">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-5 py-4">Buyer</th><th className="px-5 py-4">Seller / Ref</th><th className="px-5 py-4">Verification</th><th className="px-5 py-4">Offering</th><th className="px-5 py-4">Amount</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Received</th><th className="px-5 py-4">Invite issued</th><th className="px-5 py-4">Group joined</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {purchases
                .filter((p) => {
                  if (sellerFilter === "all") return true;
                  if (sellerFilter === "direct") return !p.affiliate;
                  return p.affiliate === sellerFilter;
                })
                .map((p) => (
                <tr key={p.id} className="text-sm text-slate-600" data-testid={`purchase-row-${p.buyer_email}`}>
                  <td className="px-5 py-4"><p className="text-[#0a0a0a]">{p.buyer_phone || p.buyer_email || "Unknown buyer"}</p>{p.buyer_email && p.buyer_phone && <p className="mt-1 text-xs text-slate-500">{p.buyer_email}</p>}{p.buyer_name && <p className="mt-1 font-mono2 text-[10px] text-slate-400">{p.buyer_name}</p>}{p.transaction_id && <p className="mt-1 font-mono2 text-[9px] text-slate-400">TXN {p.transaction_id}</p>}</td>
                  <td className="px-5 py-4">
                    {p.affiliate ? (
                      <div>
                        <span className="inline-flex rounded-full border border-black/15 bg-black/5 px-2.5 py-0.5 font-mono2 text-[10px] font-semibold text-[#0a0a0a]">
                          {p.affiliate_name || p.affiliate}
                        </span>
                        <p className="mt-0.5 font-mono2 text-[9px] text-slate-400">?ref={p.affiliate}</p>
                      </div>
                    ) : (
                      <span className="font-mono2 text-xs text-slate-400">Direct</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex rounded-full border px-2.5 py-1 font-mono2 text-[9px] font-semibold uppercase ${p.verification_source === "admin_csv" ? "border-amber-300 bg-amber-50 text-amber-800" : p.payment_verified ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-rose-300 bg-rose-50 text-rose-600"}`}>
                      {p.verification_source === "admin_csv" ? "Admin verified · CSV" : p.payment_verified ? "Verified" : "Unverified"}
                    </span>
                    {!p.payment_verified && <><p className="mt-1 max-w-[210px] text-[10px] leading-relaxed text-rose-700">{p.verification_reason || "Verification details unavailable."}</p><button type="button" onClick={() => reconcilePurchase(p.id)} disabled={busy || reconcileReason.trim().length < 5} className="mt-2 rounded-md border border-rose-300 px-2 py-1 text-[9px] font-semibold uppercase text-rose-700 disabled:opacity-40">Reconcile</button></>}
                  </td>
                  <td className="px-5 py-4 font-mono2 text-xs">{p.offering || "-"}</td>
                  <td className="px-5 py-4">{p.amount ? `₹${p.amount}` : "-"}</td>
                  <td className="px-5 py-4"><span className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-semibold uppercase ${purchaseStatusClass[p.status]}`}>{purchaseStatusLabel[p.status]}</span>{p.claimed_market && <p className="mt-1 font-mono2 text-[10px] uppercase text-slate-500">{p.claimed_market}</p>}{p.payment_verified && p.status !== "joined" && <button type="button" onClick={() => reissue("", false, p.id)} disabled={reissueBusy} className="mt-2 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase text-emerald-800 disabled:opacity-50">{reissueBusy ? "Generating…" : "Generate direct invite"}</button>}</td>
                  <td className="px-5 py-4">{formatDate(p.received_at)}</td>
                  <td className="px-5 py-4">{p.used_at ? formatDate(p.used_at) : "-"}</td>
                  <td className="px-5 py-4">{p.joined_at ? <span className="text-emerald-700">{formatDate(p.joined_at)}</span> : <span className="text-amber-700">Not confirmed</span>}</td>
                </tr>
              ))}
              {!purchases.length && <tr><td colSpan="9" className="px-5 py-12 text-center text-sm text-slate-500">No payments received yet.</td></tr>}
            </tbody>
          </table>
        </div>
        </>
        )}

        {tab === "affiliates" && <>
        <div className="glass mt-6 rounded-2xl p-6" data-testid="admin-affiliate-create">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500">Add a seller</p>
          <p className="mt-1 text-sm text-slate-600">Create a referral link and a separate login. Sellers only see their own aggregate sales.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <input
              type="text"
              value={affName}
              onChange={(event) => setAffName(event.target.value)}
              placeholder="Seller name (e.g. Rahul)"
              data-testid="admin-affiliate-name"
              className="w-full max-w-sm rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a] outline-none focus:border-black/40"
            />
            <input type="email" value={affEmail} onChange={(event) => setAffEmail(event.target.value)} placeholder="Seller login email" className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
            <input type="password" value={affPassword} onChange={(event) => setAffPassword(event.target.value)} placeholder="Initial password · 6+ chars" minLength={6} className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a] outline-none focus:border-black/40" />
            <button onClick={createAffiliate} disabled={affBusy || !affName.trim() || !affEmail.trim() || affPassword.length < 6} data-testid="admin-affiliate-create-button" className="btn-main flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-sub text-sm font-bold uppercase disabled:opacity-50">
              <UserPlus className="h-4 w-4" /> {affBusy ? "Creating" : "Create link"}
            </button>
          </div>
        </div>

        <section className="glass mt-5 rounded-2xl p-6" data-testid="admin-seller-member-lookup">
          <h2 className="font-display text-xl font-bold text-[#0a0a0a]">Find a referred member</h2>
          <p className="mt-1 text-sm text-slate-500">Search by seller and phone. “Invite issued” means the bot sent a group invite; only a confirmed Telegram join counts as joined.</p>
          <form onSubmit={lookupSellerMember} className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
            <select required value={lookupSellerCode} onChange={(event) => setLookupSellerCode(event.target.value)} className="rounded-xl border border-black/10 bg-white px-3 py-3 text-sm text-[#0a0a0a]">
              <option value="">Select seller</option>
              {affiliates.map((seller) => <option key={seller.code} value={seller.code}>{seller.name} ({seller.code})</option>)}
            </select>
            <input required type="tel" value={lookupPhone} onChange={(event) => setLookupPhone(event.target.value)} placeholder="Buyer phone (+country code)" className="rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-[#0a0a0a]" />
            <button type="submit" disabled={lookupBusy || !lookupSellerCode || !lookupPhone.trim()} className="btn-main flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-sub text-xs font-bold uppercase disabled:opacity-50"><Search className="h-4 w-4" />{lookupBusy ? "Searching" : "Find member"}</button>
          </form>
          {lookupMembers.map((member, index) => (
            <div key={`${member.phone}-${index}`} className="mt-4 rounded-xl border border-black/10 bg-white p-4" data-testid="admin-seller-member-result">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><p className="font-semibold text-[#0a0a0a]">{member.phone}</p><p className="mt-1 text-xs text-slate-600">{member.status_detail}</p></div>
                <span className={`rounded-full border px-3 py-1 font-mono2 text-[9px] font-semibold uppercase ${member.status === "joined" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : member.status === "payment_unverified" ? "border-rose-300 bg-rose-50 text-rose-700" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{member.status.replaceAll("_", " ")}</span>
              </div>
              <div className="mt-3 grid gap-2 text-[10px] text-slate-500 sm:grid-cols-4">
                <p>Payment: {member.payment_verified ? `Verified · ₹${member.payment_amount}` : "Not verified"}</p>
                <p>Claim created: {formatDate(member.created_at)}</p>
                <p>Invite issued: {formatDate(member.invite_issued_at)}</p>
                <p>Group joined: {formatDate(member.joined_at)}</p>
              </div>
              {member.can_reissue && <button type="button" onClick={() => reissue(member.phone, false, member.purchase_id)} disabled={reissueBusy} className="mt-3 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 font-mono2 text-[9px] font-semibold uppercase text-violet-800 disabled:opacity-50">{reissueBusy ? "Generating link…" : "Reissue direct group invite"}</button>}
            </div>
          ))}
          {!lookupBusy && lookupPhone && lookupMembers.some((member) => member.status === "no_referral_match") && <p className="mt-4 text-sm text-slate-500">No purchase or claim found for this phone under the selected seller.</p>}
        </section>

        <div className="glass mt-6 overflow-x-auto rounded-2xl" data-testid="admin-affiliates-table">
          <table className="w-full min-w-[860px] text-left">
            <thead className="border-b border-black/10 bg-black/[0.03] font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-5 py-4">Seller account</th><th className="px-5 py-4">Referral link</th><th className="px-5 py-4">Started claim</th><th className="px-5 py-4">Joined</th><th className="px-5 py-4">Buyers</th></tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {affiliates.map((a) => {
                const link = `${window.location.origin}/?ref=${a.code}`;
                const sellerEmail = sellerCredentials[a.code]?.email ?? a.seller_email ?? "";
                return (
                  <tr key={a.code} className="text-sm text-slate-600" data-testid={`affiliate-row-${a.code}`}>
                    <td className="px-5 py-4"><p className="font-semibold text-[#0a0a0a]">{a.name}</p><p className="mt-1 font-mono2 text-[10px] text-slate-400">{a.code}</p><p className="mt-1 text-[10px] text-slate-500">{a.account_enabled ? `Login: ${a.seller_email}` : "Login not set up"}</p>
                      <div className="mt-2 grid gap-2">
                        <input type="email" value={sellerEmail} onChange={(event) => setSellerCredentials((current) => ({ ...current, [a.code]: { ...current[a.code], email: event.target.value } }))} placeholder="Seller email" className="w-48 rounded-md border border-black/10 px-2 py-1 text-xs" />
                        <input type="password" value={sellerCredentials[a.code]?.password ?? ""} onChange={(event) => setSellerCredentials((current) => ({ ...current, [a.code]: { ...current[a.code], password: event.target.value } }))} placeholder="Set password · 6+ chars" minLength={6} className="w-48 rounded-md border border-black/10 px-2 py-1 text-xs" />
                        <button type="button" onClick={() => saveSellerCredentials(a.code)} disabled={affBusy || !sellerEmail.trim() || (sellerCredentials[a.code]?.password || "").length < 6} className="w-fit rounded-md border border-emerald-700/20 bg-emerald-50 px-2 py-1 text-[9px] font-semibold uppercase text-emerald-800 disabled:opacity-40">{a.account_enabled ? "Reset password" : "Enable login"}</button>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="max-w-[240px] truncate font-mono2 text-xs text-slate-500">{link}</span>
                        <button onClick={() => copyText(link, "Referral link copied.")} data-testid={`affiliate-copy-${a.code}`} className="flex items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-xs text-slate-600 transition-colors hover:border-black/40 hover:text-[#0a0a0a]"><Copy className="h-3 w-3" /> Copy</button>
                        <a href="/seller" target="_blank" rel="noreferrer" data-testid={`affiliate-stats-${a.code}`} className="flex items-center gap-1 rounded-lg border border-black/10 px-2.5 py-1.5 text-xs text-slate-600 transition-colors hover:border-black/40 hover:text-[#0a0a0a]"><Link2 className="h-3 w-3" /> Seller login</a>
                      </div>
                    </td>
                    <td className="px-5 py-4 font-display text-xl font-bold text-[#0a0a0a]">{a.referred}</td>
                    <td className="px-5 py-4 font-display text-xl font-bold text-emerald-600">{a.joined}</td>
                    <td className="px-5 py-4">
                      {a.members.length > 0 ? (
                        <button onClick={() => setExpanded(expanded === a.code ? "" : a.code)} data-testid={`affiliate-members-${a.code}`} className="flex items-center gap-1 font-mono2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0a0a0a]">
                          {a.member_count || a.members.length} buyer{(a.member_count || a.members.length) > 1 ? "s" : ""} {expanded === a.code ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        </button>
                      ) : <span className="text-slate-400">—</span>}
                      {expanded === a.code && (
                        <ul className="mt-2 space-y-1">
                          {a.members.map((e) => <li key={e} className="font-mono2 text-xs text-slate-600">{e}</li>)}
                        </ul>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!affiliates.length && <tr><td colSpan="5" className="px-5 py-12 text-center text-sm text-slate-500">No sellers yet — create one above.</td></tr>}
            </tbody>
          </table>
        </div>
        </>}

        {tab === "audit" && (
          <section className="glass mt-6 overflow-x-auto rounded-2xl" data-testid="admin-audit-history">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-black/10 px-5 py-4">
              <div><h2 className="font-display text-xl font-bold text-[#0a0a0a]">Admin action history</h2>
                <p className="mt-1 text-sm text-slate-500">Manual additions, overrides, payment reconciliations, and bot actions with the operator label and timestamp.</p>
              </div>
              <form onSubmit={(event) => { event.preventDefault(); setPage(1); loadAll({ tab: "audit", page: 1, auditQuery: auditSearch.trim() }); }} className="flex w-full gap-2 sm:w-auto">
                <input type="search" value={auditSearch} onChange={(event) => setAuditSearch(event.target.value)} placeholder="Search seller, buyer, reason" className="min-w-0 flex-1 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm sm:w-72" />
                <button type="submit" className="btn-main flex items-center gap-2 rounded-lg px-4 py-2 text-xs font-bold uppercase"><Search className="h-3.5 w-3.5" />Search</button>
              </form>
            </div>
            <table className="w-full min-w-[980px] text-left">
              <thead className="bg-black/[0.03] font-mono2 text-[10px] uppercase tracking-widest text-slate-500"><tr><th className="px-5 py-3">Time</th><th className="px-5 py-3">Operator</th><th className="px-5 py-3">Action</th><th className="px-5 py-3">Result</th><th className="px-5 py-3">Access for</th><th className="px-5 py-3">Details / reason</th></tr></thead>
              <tbody className="divide-y divide-black/5">{auditEvents.map((event) => (
                <tr key={event.id} className="text-sm text-slate-600"><td className="px-5 py-3">{formatDate(event.created_at)}</td><td className="px-5 py-3 font-medium text-[#0a0a0a]">{event.actor}</td><td className="px-5 py-3">{event.category} · {event.action}</td><td className={`px-5 py-3 ${event.success ? "text-emerald-700" : "text-rose-700"}`}>{event.success ? "Completed" : "Failed"}</td><td className="px-5 py-3"><div className="space-y-1">{event.target?.contact_identifier && <p>{event.target.contact_identifier}</p>}{event.target?.email && <p>{event.target.email}</p>}{event.target?.phone && <p>{event.target.phone}</p>}{event.target?.telegram_user_id != null && <p className="font-mono2 text-xs">Telegram ID {event.target.telegram_user_id}</p>}{!event.target?.contact_identifier && !event.target?.email && !event.target?.phone && event.target?.telegram_user_id == null && <span className="text-slate-400">—</span>}</div></td><td className="px-5 py-3"><p>{event.message}</p>{event.reason && <p className="mt-1 text-xs text-slate-500">Reason: {event.reason}</p>}</td></tr>
              ))}{!auditEvents.length && <tr><td colSpan="6" className="px-5 py-10 text-center text-sm text-slate-500">No admin actions have been recorded yet.</td></tr>}</tbody>
            </table>
            <div className="flex items-center justify-between gap-3 border-t border-black/10 px-5 py-3 text-xs text-slate-500"><span>{auditPagination.total} actions · page {auditPagination.page} of {Math.max(1, auditPagination.pages)}</span><div className="flex gap-2"><button type="button" onClick={() => loadAll({ tab: "audit", page: Math.max(1, auditPagination.page - 1) })} disabled={auditPagination.page <= 1 || loading} className="rounded-md border border-black/10 px-3 py-2 disabled:opacity-40">Previous</button><button type="button" onClick={() => loadAll({ tab: "audit", page: Math.min(auditPagination.pages, auditPagination.page + 1) })} disabled={auditPagination.page >= auditPagination.pages || loading} className="rounded-md border border-black/10 px-3 py-2 disabled:opacity-40">Next</button></div></div>
          </section>
        )}

        {tab !== "overview" && tab !== "audit" && renderPagination()}
        <div className="glass mt-8 rounded-2xl p-6" data-testid="admin-reissue-card">
          <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-500">Buyer support</p>
          <h2 className="mt-2 font-sub text-lg font-semibold text-[#0a0a0a]">Generate a direct group invite</h2>
          <p className="mt-1 text-sm text-slate-600">
            For buyers with a pending or expired invite. They can join the group directly without starting the bot; Telegram will confirm their entry automatically.
          </p>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <input
              type="text"
              value={reissueIdentifier}
              onChange={(event) => setReissueIdentifier(event.target.value)}
              placeholder={reissueManualOverride ? "Phone, email, or optional member label" : "Buyer phone (+country code) or payment email"}
              data-testid="admin-reissue-email"
              className="flex-1 rounded-xl border border-black/10 bg-white px-4 py-3 font-mono2 text-xs text-[#0a0a0a] outline-none focus:border-black/40"
            />
            <button
              onClick={reissue}
              disabled={reissueBusy || (!reissueIdentifier.trim() && !reissueManualOverride) || (reissueManualOverride && reissueOverrideReason.trim().length < 5)}
              data-testid="admin-reissue-button"
              className="btn-main rounded-xl px-5 py-3 font-sub text-sm font-bold uppercase disabled:opacity-50"
            >
              {reissueBusy ? "Generating" : reissueManualOverride ? "Generate override invite" : "Generate direct invite"}
            </button>
          </div>
          <label className="mt-4 flex items-start gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={reissueManualOverride}
              onChange={(event) => setReissueManualOverride(event.target.checked)}
              data-testid="admin-reissue-manual-override"
              className="mt-1 h-4 w-4 accent-rose-700"
            />
            <span>Allow access without a verified payment (admin exception)</span>
          </label>
          {reissueManualOverride && (
            <div className="mt-3 rounded-xl border border-rose-300 bg-rose-50 p-4" data-testid="admin-reissue-override-warning">
              <p className="text-sm font-semibold text-rose-800">This bypass grants group access even if the buyer has not paid.</p>
              <p className="mt-1 text-xs text-rose-700">The link and resulting membership will be labeled and logged as an admin override. Phone, email, or member label is optional; Telegram ID is recorded when they join. Enter the reason to continue.</p>
              <input
                type="text"
                value={reissueOverrideReason}
                onChange={(event) => setReissueOverrideReason(event.target.value)}
                placeholder="Reason for this exception"
                maxLength={500}
                data-testid="admin-reissue-override-reason"
                className="mt-3 w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-[#0a0a0a] outline-none focus:border-rose-500"
              />
            </div>
          )}
          {reissueLink && (
            <div className="mt-4 flex flex-col gap-3 rounded-xl border border-black/10 bg-black/[0.03] px-4 py-3 sm:flex-row sm:items-center" data-testid="admin-reissue-result">
              <p className="flex-1 break-all font-mono2 text-xs text-slate-700">{reissueLink}</p>
              <button
                onClick={() => copyText(reissueLink, "Link copied — send it to the buyer.")}
                data-testid="admin-reissue-copy"
                className="rounded-lg border border-black/10 px-3 py-2 text-xs text-slate-600 transition-colors hover:border-black/40 hover:text-[#0a0a0a]"
              >
                Copy
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-3 text-xs text-slate-500"><ShieldCheck className="h-4 w-4" /> Expired members receive a renewal DM, remain for {graceDays} days, then are kicked automatically.</div>
        <button onClick={openPanel} disabled={busy || !adminKey} className="mt-5 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest text-slate-500 hover:text-[#0a0a0a] disabled:opacity-40"><RefreshCcw className="h-3.5 w-3.5" /> Refresh data</button>
        </>}
      </div>
    </main>
  );
};

export default Admin;
